import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import {
  Truck, Package, Wrench, AlertTriangle, Filter,
  Search, DollarSign,
  Clock, User, LogIn, FileText,
  PackageSearch, CalendarDays, FileSpreadsheet, ChevronDown, ChevronRight, Layers, TrendingUp,
  BarChart3, Info, FileSignature, Eye, X, History, ChevronUp, Activity, ClipboardCheck
} from 'lucide-react';
import { getLaporan, getLaporanPenggunaanSparepart, getLaporanAnalisaCm, getLaporanKendaraanDetail, getLaporanKendaraanFoto, getLaporanKendaraan } from '../api';
import MultiSelectFilter from '../components/MultiSelectFilter';

const COLORS = ['#11cdef', '#14b8c4', '#0d7a8a', '#2dce89', '#fb6340', '#f5365c', '#0a3845', '#073845'];

// --- Custom Tooltip ---
function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="tooltip-item" style={{ color: p.color }}>
            {p.name}: <strong>{p.value?.toLocaleString()}</strong>
          </p>
        ))}
      </div>
    );
  }
  return null;
}

// --- Stat Card Mini ---
function StatCardMini({ icon: Icon, label, value, color, sublabel }) {
  return (
    <div className="stat-card">
      <div className="stat-card-body">
        <div className="stat-card-content">
          <h5 className="stat-card-label">{label}</h5>
          <span className="stat-card-value">{value?.toLocaleString() || 0}</span>
          {sublabel && <span className="stat-card-sublabel">{sublabel}</span>}
        </div>
        <div className="stat-card-icon-col">
          <div className="stat-icon-shape" style={{ background: color + '18', color }}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Chart Card ---
function ChartCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`chart-card ${className}`}>
      <div className="chart-card-header">
        <h6 className="chart-card-title">{title}</h6>
        {subtitle && <span className="chart-card-subtitle">{subtitle}</span>}
      </div>
      <div className="chart-card-body">{children}</div>
    </div>
  );
}

// --- Tab Button ---
function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 20px', border: 'none', borderRadius: 8,
        fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
        background: active ? 'var(--primary)' : 'white',
        color: active ? 'white' : 'var(--gray-600)',
        boxShadow: active ? '0 4px 12px rgba(94,114,228,0.3)' : '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'all 0.2s',
      }}
    >
      <Icon size={18} />
      {children}
    </button>
  );
}

// --- PDF Helper: Header + Footer ---
function pdfHeader(doc, title, subtitle, pageW) {
  const margin = 15;
  const primary = [17, 205, 239]; // #11cdef
  doc.setFillColor(...primary);
  doc.rect(0, 0, pageW, 38, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, 18);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(subtitle, margin, 28);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Layanan Synexa', pageW - margin, 18, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Sistem Informasi Armada', pageW - margin, 25, { align: 'right' });
}

function pdfFooter(doc, pageW) {
  const margin = 15;
  const gray = [136, 152, 170];
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const h = doc.internal.pageSize.getHeight();
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, h - 20, pageW - margin, h - 20);
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    const now = new Date();
    const dateStr = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    doc.text(`Dicetak pada: ${dateStr}`, margin, h - 12);
    doc.text(`Halaman ${i} dari ${pageCount}`, pageW - margin, h - 12, { align: 'right' });
  }
}

function pdfStatBox(doc, x, y, w, label, value, color) {
  doc.setFillColor(color[0], color[1], color[2]);
  doc.rect(x, y + 5, 3, 18, 'F');
  doc.setTextColor(136, 152, 170);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(label, x + 8, y + 12);
  doc.setTextColor(23, 43, 77);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(String(value), x + 8, y + 24);
}

function pdfAutoTableOpts(startY, margin) {
  return {
    startY,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
    headStyles: { fillColor: [94, 114, 228], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8, halign: 'center' },
    bodyStyles: { fontSize: 8 },
  };
}

// --- Badge Status ---
function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  let bg = '#e9ecef', color = '#525f7f';
  if (s === 'valid' || s === 'aktif' || s === 'selesai' || s === 'diverifikasi') {
    bg = '#d1fae5'; color = '#065f46';
  } else if (s === 'expired' || s === 'nonaktif' || s === 'dibatalkan') {
    bg = '#fee2e2'; color = '#991b1b';
  } else if (s === 'direncanakan' || s === 'dilaporkan') {
    bg = '#dbeafe'; color = '#1e40af';
  } else if (s === 'berlangsung' || s === 'dalam_pengerjaan' || s === 'dalam pengerjaan') {
    bg = '#fef3c7'; color = '#92400e';
  } else if (s === 'hadir') {
    bg = '#d1fae5'; color = '#065f46';
  } else if (s === 'izin') {
    bg = '#dbeafe'; color = '#1e40af';
  } else if (s === 'sakit') {
    bg = '#fef3c7'; color = '#92400e';
  } else if (s === 'cuti') {
    bg = '#f3e8ff'; color = '#6d28d9';
  } else if (s === 'alpha') {
    bg = '#fee2e2'; color = '#991b1b';
  }
  return (
    <span className="badge" style={{ background: bg, color }}>
      {status || '-'}
    </span>
  );
}

// =============================================
// TAB: KENDARAAN
// =============================================
function TabKendaraan({ data: initialData }) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState('desc');
  // Armada yang sedang dibuka detail perjalanannya (modal)
  const [detailVehicle, setDetailVehicle] = useState(null);

  // ===== Filter Nama Klien & No Kontrak — dijalankan di SERVER =====
  // Server-side supaya angka ringkasan (total, wilayah, status dokumen, dokumen
  // kedaluwarsa) selalu cocok dengan tabelnya — bukan cuma menyaring baris yang
  // sudah dimuat browser. Sumber: vehicles.operator (klien) & vehicles.contract_no.
  const [filterKlien, setFilterKlien] = useState([]);
  const [filterNoKontrak, setFilterNoKontrak] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ klien: [], no_kontrak: [] });
  const [serverData, setServerData] = useState(null);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [filterError, setFilterError] = useState('');

  const adaFilter = filterKlien.length > 0 || filterNoKontrak.length > 0;
  // Hasil terbaru dari server; kalau belum ada, pakai data awal dari /api/laporan
  const data = serverData || initialData;

  useEffect(() => {
    let batal = false;
    const params = {};
    if (filterKlien.length > 0) params.klien = filterKlien.join(',');
    if (filterNoKontrak.length > 0) params.no_kontrak = filterNoKontrak.join(',');
    setLoadingFilter(true);
    getLaporanKendaraan(params)
      .then(res => {
        if (batal) return;
        const d = res?.data || res;
        setServerData(d);
        if (d?.filter_options) setFilterOptions(d.filter_options);
        setFilterError('');
      })
      .catch(err => {
        if (batal) return;
        // Gagal (mis. tanpa izin menu) → tampilkan pesan, data awal tetap dipakai
        setFilterError(err?.response?.data?.error || err?.message || 'Gagal memuat data.');
      })
      .finally(() => { if (!batal) setLoadingFilter(false); });
    return () => { batal = true; };
  }, [filterKlien, filterNoKontrak]);

  const resetFilter = () => { setFilterKlien([]); setFilterNoKontrak([]); };

  const filterLabel = [
    filterKlien.length > 0 ? `Klien: ${filterKlien.join(', ')}` : '',
    filterNoKontrak.length > 0 ? `No Kontrak: ${filterNoKontrak.join(', ')}` : '',
  ].filter(Boolean).join(' · ');

  const generatePDF = () => {
    if (!data) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;

    pdfHeader(doc, 'LAPORAN KENDARAAN',
      `Total: ${data.total} kendaraan terdaftar${filterLabel ? ' — Filter: ' + filterLabel : ''}`, pageW);

    // Summary stats
    const startY = 48;
    doc.setDrawColor(230, 230, 230);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 28, 3, 3, 'FD');
    const stnkExp = data.by_status.find(s => s.type === 'stnk')?.expired || 0;
    const kirExp = data.by_status.find(s => s.type === 'kir')?.expired || 0;
    const kpExp = data.by_status.find(s => s.type === 'kp')?.expired || 0;
    const statW = (pageW - 2 * margin - 20) / 4;
    pdfStatBox(doc, margin + 5, startY, statW, 'Total Kendaraan', data.total, [17, 205, 239]);
    pdfStatBox(doc, margin + statW + 11, startY, statW, 'STNK Expired', stnkExp, [245, 54, 92]);
    pdfStatBox(doc, margin + 2 * (statW + 6) + 5, startY, statW, 'KIR Expired', kirExp, [251, 99, 64]);
    pdfStatBox(doc, margin + 3 * (statW + 6) + 5, startY, statW, 'KP Expired', kpExp, [245, 54, 92]);

    // Table
    const headers = [['No', 'No. Polisi', 'No. Body', 'Merek', 'Tipe', 'Wilayah', 'Nama Klien', 'No Kontrak', 'STNK', 'KIR', 'KP']];
    const rows = data.data.map((v, i) => [
      (i + 1).toString(), v.plate_no || '-', v.body_no || '-', v.brand || '-', v.type || '-', v.region || '-',
      v.operator || '-', v.contract_no || '(Tanpa No Kontrak)', v.stnk || '-', v.kir || '-', v.kp || '-',
    ]);

    autoTable(doc, {
      ...pdfAutoTableOpts(startY + 40, margin),
      head: headers,
      body: rows,
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { fontStyle: 'bold' },
        8: { halign: 'center' },
        9: { halign: 'center' },
        10: { halign: 'center' },
      },
    });

    pdfFooter(doc, pageW);
    doc.save('Laporan_Kendaraan.pdf');
  };

  if (!data) return <div className="page-loading"><div className="spinner-lg" /><p>Memuat data...</p></div>;

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = data.data.filter(v => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (v.plate_no || '').toLowerCase().includes(q) ||
           (v.body_no || '').toLowerCase().includes(q) ||
           (v.brand || '').toLowerCase().includes(q) ||
           (v.region || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const aRaw = a[sortField];
    const bRaw = b[sortField];
    // Numeric sort for 'id' field
    if (sortField === 'id') {
      return sortDir === 'asc' ? (aRaw || 0) - (bRaw || 0) : (bRaw || 0) - (aRaw || 0);
    }
    const aVal = (aRaw || '').toString().toLowerCase();
    const bVal = (bRaw || '').toString().toLowerCase();
    return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  // Document status summary pie
  const docPieData = data.by_status.flatMap(d => [
    { name: d.label + ' Valid', value: d.valid, color: '#2dce89' },
    { name: d.label + ' Expired', value: d.expired, color: '#f5365c' },
  ]);

  return (
    <div>
      {/* Stats Row */}
      <div className="dash-stats-row" style={{ marginBottom: 20 }}>
        <div className="dash-stat-col">
          <StatCardMini icon={Truck} label="Total Kendaraan" value={data.total} color="#11cdef" sublabel="Terdaftar" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={AlertTriangle} label="STNK Expired" value={data.by_status.find(s => s.type === 'stnk')?.expired || 0} color="#f5365c" sublabel="Perlu perpanjangan" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={AlertTriangle} label="KIR Expired" value={data.by_status.find(s => s.type === 'kir')?.expired || 0} color="#fb6340" sublabel="Perlu perpanjangan" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={AlertTriangle} label="KP Expired" value={data.by_status.find(s => s.type === 'kp')?.expired || 0} color="#f5365c" sublabel="Perlu perpanjangan" />
        </div>
      </div>

      {/* Charts Row */}
      <div className="dash-charts-row" style={{ marginBottom: 20 }}>
        {/* Bar: By Region */}
        <div className="dash-chart-col-4">
          <ChartCard title="Per Wilayah" subtitle={`${data.by_region.length} wilayah`}>
            {data.by_region.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_region} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                  <XAxis dataKey="region" stroke="#8898aa" fontSize={11} angle={-15} textAnchor="end" height={60} />
                  <YAxis stroke="#8898aa" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={24}>
                    {data.by_region.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>

        {/* Pie: Document Status */}
        <div className="dash-chart-col-4">
          <ChartCard title="Status Dokumen" subtitle="Valid vs Expired">
            {docPieData.length > 0 ? (
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={docPieData}
                      cx="50%" cy="50%"
                      innerRadius={55} outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {docPieData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-legend" style={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                  {docPieData.map((d, i) => (
                    <div key={i} className="donut-legend-item">
                      <span className="donut-dot" style={{ background: d.color }} />
                      <span>{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>

        {/* Bar: By Brand */}
        <div className="dash-chart-col-4">
          <ChartCard title="Merek Kendaraan" subtitle={`${data.by_brand.length} merek`}>
            {data.by_brand.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_brand} layout="vertical" margin={{ top: 5, right: 20, left: 70, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" horizontal={false} />
                  <XAxis type="number" stroke="#8898aa" fontSize={11} />
                  <YAxis type="category" dataKey="brand" stroke="#8898aa" fontSize={10} width={65} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={16}>
                    {data.by_brand.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>
      </div>

      {/* ====== FILTER: NAMA KLIEN & NO KONTRAK (server-side) ====== */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 12, padding: '8px 12px', borderRadius: 10,
        background: '#fff', border: '1px solid var(--gray-100)'
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)' }}>
          <Filter size={14} /> Filter
        </span>
        <MultiSelectFilter
          options={filterOptions.klien}
          selected={filterKlien}
          onChange={setFilterKlien}
          placeholder="Nama Klien"
          searchPlaceholder="Cari klien..."
        />
        <MultiSelectFilter
          options={filterOptions.no_kontrak}
          selected={filterNoKontrak}
          onChange={setFilterNoKontrak}
          placeholder="No Kontrak"
          searchPlaceholder="Cari no kontrak..."
        />
        {adaFilter && (
          <button className="btn-icon" onClick={resetFilter} style={{ color: '#f5365c' }} title="Reset filter">
            <X size={14} />
          </button>
        )}
        <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginLeft: 'auto' }}>
          {loadingFilter
            ? 'Memuat...'
            : adaFilter
              ? `Menampilkan ${data.total} armada (dari ${data.total_semua || data.total})`
              : 'Tanpa filter: semua armada'}
        </span>
      </div>
      {filterError && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontSize: '0.78rem' }}>
          {filterError}
        </div>
      )}
      {adaFilter && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: '#e0f2fe', color: '#075985', fontSize: '0.78rem' }}>
          <strong>Filter aktif:</strong> {filterLabel}. Ringkasan (total, per wilayah, status dokumen) ikut disaring ke armada tersebut.
        </div>
      )}

      {/* Table */}
      <div className="page-header-row" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          Data Kendaraan ({data.total})
        </h3>
        <div className="page-header-actions">
          <div className="search-box">
            <Search size={16} />
            <input placeholder="Cari plat, body, merek..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={generatePDF} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={16} /><span>Export PDF</span>
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('plate_no')}>
                No. Polisi {sortField === 'plate_no' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('body_no')}>
                No. Body {sortField === 'body_no' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('brand')}>
                Merek {sortField === 'brand' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th>Tipe</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('region')}>
                Wilayah {sortField === 'region' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('operator')}>
                Nama Klien {sortField === 'operator' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('contract_no')}>
                No Kontrak {sortField === 'contract_no' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
              </th>
              <th>STNK</th>
              <th>KIR</th>
              <th>KP</th>
              <th style={{ textAlign: 'center' }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(v => (
              <tr key={v.id}>
                <td><strong>{v.plate_no || '-'}</strong></td>
                <td>{v.body_no || '-'}</td>
                <td>{v.brand || '-'}</td>
                <td>{v.type || '-'}</td>
                <td><span className="region-tag">{v.region || '-'}</span></td>
                <td style={{ fontSize: '0.78rem', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v.operator || ''}>
                  {v.operator || '-'}
                </td>
                <td style={{ fontSize: '0.78rem' }}>
                  {v.contract_no || <span style={{ color: 'var(--gray-400)' }}>(Tanpa No Kontrak)</span>}
                </td>
                <td><StatusBadge status={v.stnk} /></td>
                <td><StatusBadge status={v.kir} /></td>
                <td><StatusBadge status={v.kp} /></td>
                <td style={{ textAlign: 'center' }}>
                  <button
                    className="btn-icon"
                    title="Detail perjalanan armada (PM, CM, sparepart, teknisi)"
                    aria-label={`Detail perjalanan ${v.plate_no || v.id}`}
                    onClick={() => setDetailVehicle(v)}
                    style={{ color: '#11cdef' }}
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={11} className="text-center text-muted" style={{ padding: 40 }}>Tidak ada data kendaraan.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detailVehicle && (
        <VehicleJourneyModal
          vehicle={detailVehicle}
          onClose={() => setDetailVehicle(null)}
        />
      )}
    </div>
  );
}

// =============================================
// DETAIL PERJALANAN ARMADA (modal dari tab Kendaraan)
// Menggabungkan riwayat PM + CM satu armada: tanggal, teknisi, sparepart,
// jasa, odometer, biaya, dan kondisi dokumen.
// =============================================
function JourneySection({ icon: Icon, title, subtitle, color = '#11cdef', children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ width: 26, height: 26, borderRadius: 8, background: color + '18', color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          <Icon size={15} />
        </span>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--gray-600)' }}>{title}</div>
          {subtitle && <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>{subtitle}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

function JourneyStat({ label, value, sub, color }) {
  return (
    <div style={{ border: '1px solid #eef0f4', borderRadius: 10, padding: '10px 12px', background: '#fff' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</div>
      <div style={{ fontSize: '1.15rem', fontWeight: 800, color: color || 'var(--gray-700)', lineHeight: 1.3 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.68rem', color: 'var(--gray-400)' }}>{sub}</div>}
    </div>
  );
}

function docTone(status) {
  const s = (status || '').toUpperCase();
  if (s === 'VALID') return '#2dce89';
  if (s === 'EXPIRED') return '#f5365c';
  return '#8898aa';
}

function VehicleJourneyModal({ vehicle, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [timelineFilter, setTimelineFilter] = useState('semua');
  // Foto bukti per aktivitas: { 'CM-126': { status: 'loading'|'done', urls: [{idx, url}|{idx, error}] } }
  const [photoState, setPhotoState] = useState({});
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getLaporanKendaraanDetail({ id: vehicle.id })
      .then(res => { if (alive) setData(res.data); })
      .catch(err => { if (alive) setError(err.message || 'Gagal memuat riwayat armada.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [vehicle.id]);

  // Foto disimpan sebagai data-URI base64 di server, jadi tidak bisa dipakai
  // langsung sebagai <img src> (butuh header Authorization). Ambil per aktivitas
  // saat barisnya dibuka, lalu tampilkan sebagai thumbnail dari object URL.
  const objectUrls = React.useRef([]);
  useEffect(() => () => {
    objectUrls.current.forEach(u => { try { window.URL.revokeObjectURL(u); } catch (e) { /* diabaikan */ } });
    objectUrls.current = [];
  }, []);

  const info = data?.vehicle || {};
  const sum = data?.summary || {};
  const timeline = (data?.timeline || []).filter(it =>
    timelineFilter === 'semua' ? true : it.tipe === timelineFilter
  );

  const loadPhotos = async (it) => {
    const key = `${it.tipe}-${it.id}`;
    if (photoState[key]) return;
    const total = it.jumlah_foto || 0;
    setPhotoState(prev => ({ ...prev, [key]: { status: 'loading', urls: [] } }));
    const loaded = [];
    for (let i = 0; i < total; i++) {
      try {
        const res = await getLaporanKendaraanFoto({ tipe: it.tipe, id: it.id, idx: i });
        const url = window.URL.createObjectURL(res.data);
        objectUrls.current.push(url);
        loaded.push({ idx: i, url });
      } catch (e) {
        loaded.push({ idx: i, error: true });
      }
      setPhotoState(prev => ({ ...prev, [key]: { status: 'loading', urls: [...loaded] } }));
    }
    setPhotoState(prev => ({ ...prev, [key]: { status: 'done', urls: loaded } }));
  };

  const toggleItem = (key, it) => {
    const akanDibuka = !openItems[key];
    setOpenItems(prev => ({ ...prev, [key]: akanDibuka }));
    if (akanDibuka && it && (it.jumlah_foto || 0) > 0) loadPhotos(it);
  };

  const namaFile = () => {
    const plat = (info.plate_no || vehicle.plate_no || String(vehicle.id)).replace(/[^A-Za-z0-9-]/g, '');
    const tgl = new Date().toISOString().slice(0, 10);
    return { plat, tgl };
  };

  // ---------- Export CSV ----------
  const exportCSV = () => {
    if (!data) return;
    const { plat, tgl } = namaFile();
    const rows = [
      ['PERJALANAN ARMADA'],
      ['No. Polisi', info.plate_no || '-', 'No. Body', info.body_no || '-'],
      ['Nama Klien', info.operator || '-', 'No. Kontrak', info.contract_no || '-'],
      ['Rute', info.route_code || '-', 'Wilayah', info.region || '-'],
      ['Jenis Armada', info.type || '-', 'AC/Non AC', info.jenis_ac || '-'],
      ['Merek / Tahun', [info.brand, info.tahun].filter(Boolean).join(' / ') || '-', 'Teknisi Armada', info.teknisi || '-'],
      ['Pemilik', info.owner_name || '-', 'Pengendapan', info.pengendapan || '-'],
      ['Status Armada', info.is_active ? 'Aktif' : 'Non-Aktif', 'Hari Sejak Servis', sum.hari_sejak_servis > 0 ? sum.hari_sejak_servis : '-'],
      ['Periode Riwayat', sum.periode_awal ? `${sum.periode_awal} s.d. ${sum.periode_akhir}` : '-', 'Odometer Terakhir', sum.odometer_terakhir || '-'],
      [],
      ['DOKUMEN', 'Status', 'Berlaku Sampai'],
      ['STNK', info.stnk || '-', info.stnk_until || '-'],
      ['KIR', info.kir || '-', info.kir_until || '-'],
      ['KP', info.kp || '-', info.kp_until || '-'],
      ['Asuransi', info.insurance_status || '-', info.insurance_until || '-'],
      [],
      ['RINGKASAN'],
      ['Total PM', sum.total_pm || 0],
      ['Total CM', sum.total_cm || 0],
      ['Total Aktivitas', sum.total_aktivitas || 0],
      ['PM Selesai', sum.pm_selesai || 0],
      ['CM Selesai', sum.cm_selesai || 0],
      ['Jenis Sparepart Terpakai', sum.jumlah_sparepart || 0],
      ['Total Qty Sparepart', sum.total_sparepart_qty || 0],
      ['Total Nilai Sparepart (Rp)', Math.round(sum.total_sparepart_nilai || 0)],
      ['Item Jasa', sum.total_jasa_item || 0],
      ['Total Nominal Tagihan CM (Rp)', Math.round(sum.total_nominal || 0)],
      ['Jumlah Teknisi', sum.jumlah_teknisi || 0],
      ['Jumlah Klien', sum.jumlah_klien || 0],
      ['Jumlah Foto Bukti', sum.jumlah_foto || 0],
      [],
      ['SPAREPART YANG PERNAH DIPAKAI'],
      ['Nama Sparepart', 'Qty', 'Harga (Rp)', 'Total (Rp)', 'Berapa Kali', 'Terakhir'],
      ...(data.spareparts || []).map(s => [s.nama, s.qty, Math.round(s.harga), Math.round(s.nilai), s.transaksi, s.terakhir || '-']),
      [],
      ['TEKNISI YANG MENANGANI'],
      ['Nama Teknisi', 'PM', 'CM', 'Pendamping', 'Total', 'Terakhir'],
      ...(data.by_teknisi || []).map(t => [t.nama, t.pm, t.cm, t.pendamping || 0, t.total, t.terakhir || '-']),
      [],
      ['AKTIVITAS PER BULAN'],
      ['Bulan', 'PM', 'CM', 'Total'],
      ...(data.by_bulan || []).map(b => [b.label, b.pm, b.cm, b.total]),
      [],
      ['TIMELINE AKTIVITAS'],
      ['Tipe', 'No. Ref', 'Tanggal', 'Tanggal Selesai', 'Jenis', 'Teknisi', 'Pendamping', 'Status', 'Odometer', 'Kode Klien', 'Lokasi', 'Qty SP', 'Nilai SP (Rp)', 'Nominal (Rp)', 'Jumlah Foto'],
      ...(data.timeline || []).map(it => [it.tipe, it.no_ref, it.tanggal, it.tanggal_selesai || '-', it.jenis || '-', it.teknisi || '-',
        it.teknisi_pendamping || '-', it.status || '-', it.odometer || 0, it.klien || '-', it.lokasi || '-',
        it.qty_sparepart || 0, Math.round(it.nilai_sparepart || 0), Math.round(it.nominal || 0), it.jumlah_foto || 0]),
    ];

    // Rincian sparepart & jasa tiap aktivitas (hanya yang punya)
    (data.timeline || []).forEach((it, n) => {
      if (it.spareparts.length > 0) {
        rows.push([], [`RINCIAN SPAREPART ${it.tipe} ${it.no_ref} (${it.tanggal})`], ['Nama', 'Qty', 'Harga (Rp)', 'Subtotal (Rp)']);
        it.spareparts.forEach(p => rows.push([p.nama, p.jumlah, Math.round(p.harga), Math.round(p.subtotal)]));
      }
      if (it.jasa.length > 0) {
        rows.push([], [`RINCIAN JASA ${it.tipe} ${it.no_ref} (${it.tanggal})`], ['Nama', 'Qty', 'Harga (Rp)', 'Subtotal (Rp)']);
        it.jasa.forEach(p => rows.push([p.nama, p.jumlah, Math.round(p.harga), Math.round(p.subtotal)]));
      }
      if (n === (data.timeline || []).length - 1) rows.push([]);
    });

    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Perjalanan_Armada_${plat}_${tgl}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // ---------- Export PDF ----------
  const generatePDF = () => {
    if (!data) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    const { plat } = namaFile();

    const subJudul = [info.plate_no, info.body_no, info.operator, info.contract_no ? `Kontrak ${info.contract_no}` : ''].filter(Boolean).join(' · ');
    pdfHeader(doc, 'PERJALANAN ARMADA', `${subJudul} · Dicetak ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, pageW);

    const startY = 46;
    doc.setDrawColor(230, 230, 230);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 28, 3, 3, 'FD');
    const statW = (pageW - 2 * margin - 20) / 4;
    pdfStatBox(doc, margin + 5, startY, statW, `Total Aktivitas (PM ${sum.total_pm || 0}/CM ${sum.total_cm || 0})`, sum.total_aktivitas || 0, [17, 205, 239]);
    pdfStatBox(doc, margin + statW + 11, startY, statW, 'Total Sparepart (Qty)', sum.total_sparepart_qty || 0, [137, 101, 224]);
    pdfStatBox(doc, margin + 2 * (statW + 6) + 5, startY, statW, 'Total Biaya CM', `Rp ${Math.round(sum.total_nominal || 0).toLocaleString('id-ID')}`, [245, 54, 92]);
    pdfStatBox(doc, margin + 3 * (statW + 6) + 5, startY, statW, 'Teknisi Terlibat', sum.jumlah_teknisi || 0, [251, 99, 64]);

    let y = startY + 40;
    autoTable(doc, {
      ...pdfAutoTableOpts(y, margin),
      head: [['Identitas', 'Keterangan', 'Dokumen', 'Status', 'Berlaku Sampai']],
      body: [
        ['Nama Klien', info.operator || '-', 'STNK', info.stnk || '-', info.stnk_until || '-'],
        ['No. Polisi / Body', `${info.plate_no || '-'} / ${info.body_no || '-'}`, 'KIR', info.kir || '-', info.kir_until || '-'],
        ['Rute / Wilayah', `${info.route_code || '-'} / ${info.region || '-'}`, 'KP', info.kp || '-', info.kp_until || '-'],
        ['Jenis / AC', `${info.type || '-'} / ${info.jenis_ac || '-'}`, 'Asuransi', info.insurance_status || '-', info.insurance_until || '-'],
        ['No. Kontrak', info.contract_no || '-', 'Periode Riwayat', sum.periode_awal ? `${sum.periode_awal} s.d. ${sum.periode_akhir}` : '-', ''],
        ['Pemilik / Pengendapan', `${info.owner_name || '-'} / ${info.pengendapan || '-'}`, 'Odometer Terakhir', sum.odometer_terakhir ? `${sum.odometer_terakhir} km` : '-', ''],
        ['Teknisi Armada', info.teknisi || '-', 'Hari Sejak Servis', sum.hari_sejak_servis > 0 ? `${sum.hari_sejak_servis} hari` : '-', ''],
      ],
      columnStyles: { 0: { cellWidth: 55, fontStyle: 'bold' }, 2: { cellWidth: 35, fontStyle: 'bold' } },
    });

    y = doc.lastAutoTable.finalY + 12;
    doc.setTextColor(23, 43, 77);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('SPAREPART YANG PERNAH DIPAKAI', margin, y);
    autoTable(doc, {
      ...pdfAutoTableOpts(y + 4, margin),
      head: [['Nama Sparepart', 'Qty', 'Harga (Rp)', 'Total (Rp)', 'Dipakai', 'Terakhir']],
      body: (data.spareparts || []).length
        ? data.spareparts.map(s => [s.nama, fmtQty(s.qty), Math.round(s.harga).toLocaleString('id-ID'), Math.round(s.nilai).toLocaleString('id-ID'), `${s.transaksi}x`, s.terakhir || '-'])
        : [['(Belum ada pemakaian sparepart)', '', '', '', '', '']],
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'center' } },
    });

    y = doc.lastAutoTable.finalY + 12;
    doc.setTextColor(23, 43, 77);
    doc.setFont('helvetica', 'bold');
    doc.text('TEKNISI YANG MENANGANI', margin, y);
    autoTable(doc, {
      ...pdfAutoTableOpts(y + 4, margin),
      head: [['Nama Teknisi', 'PM', 'CM', 'Pendamping', 'Total', 'Terakhir']],
      body: (data.by_teknisi || []).length
        ? data.by_teknisi.map(t => [t.nama, t.pm, t.cm, t.pendamping || 0, t.total, t.terakhir || '-'])
        : [['(Belum ada data teknisi)', '', '', '', '', '']],
      columnStyles: { 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' } },
    });

    y = doc.lastAutoTable.finalY + 12;
    doc.setFont('helvetica', 'bold');
    doc.text('TIMELINE AKTIVITAS (PM & CM)', margin, y);
    autoTable(doc, {
      ...pdfAutoTableOpts(y + 4, margin),
      head: [['Tipe', 'No. Ref', 'Tanggal', 'Jenis Pekerjaan', 'Teknisi', 'Status', 'Odometer', 'Qty SP', 'Nilai SP (Rp)', 'Nominal (Rp)']],
      body: (data.timeline || []).length
        ? data.timeline.map(it => [it.tipe, it.no_ref, it.tanggal, it.jenis || '-', it.teknisi || '-', it.status || '-',
          it.odometer ? it.odometer.toLocaleString('id-ID') : '-', it.qty_sparepart || 0,
          Math.round(it.nilai_sparepart || 0).toLocaleString('id-ID'), Math.round(it.nominal || 0).toLocaleString('id-ID')])
        : [['(Belum ada aktivitas)', '', '', '', '', '', '', '', '', '']],
      columnStyles: { 0: { halign: 'center', cellWidth: 14 }, 7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' } },
    });

    // Rincian sparepart & jasa per aktivitas yang punya isi
    (data.timeline || []).forEach(it => {
      if (it.spareparts.length === 0 && it.jasa.length === 0) return;
      y = doc.lastAutoTable.finalY + 12;
      doc.setTextColor(23, 43, 77);
      doc.setFont('helvetica', 'bold');
      doc.text(`RINCIAN ${it.tipe} ${it.no_ref} — ${it.tanggal || '-'} — Teknisi: ${it.teknisi || '-'}`, margin, y);
      const body = [];
      if (it.spareparts.length) {
        body.push([{ content: 'SPAREPART', colSpan: 4, styles: { fontStyle: 'bold', fillColor: [237, 233, 254] } }]);
        it.spareparts.forEach(p => body.push([p.nama, fmtQty(p.jumlah), Math.round(p.harga).toLocaleString('id-ID'), Math.round(p.subtotal).toLocaleString('id-ID')]));
      }
      if (it.jasa.length) {
        body.push([{ content: 'JASA', colSpan: 4, styles: { fontStyle: 'bold', fillColor: [254, 243, 199] } }]);
        it.jasa.forEach(p => body.push([p.nama, fmtQty(p.jumlah), Math.round(p.harga).toLocaleString('id-ID'), Math.round(p.subtotal).toLocaleString('id-ID')]));
      }
      autoTable(doc, {
        ...pdfAutoTableOpts(y + 4, margin),
        head: [['Nama', 'Qty', 'Harga (Rp)', 'Subtotal (Rp)']],
        body,
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
      });
    });

    pdfFooter(doc, pageW);
    doc.save(`Perjalanan_Armada_${plat}.pdf`);
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 1000, width: '95%', maxHeight: '92vh', overflow: 'auto', borderRadius: 12 }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #11cdef, #0d7a8a)', color: '#fff', borderRadius: '12px 12px 0 0', padding: '14px 20px', position: 'sticky', top: 0, zIndex: 10 }}>
          <div>
            <h3 style={{ color: '#fff', margin: 0, fontSize: '1rem' }}>
              Perjalanan Armada — {info.plate_no || vehicle.plate_no || `#${vehicle.id}`}
            </h3>
            <small style={{ opacity: 0.9 }}>
              {[info.body_no, info.brand, info.type, info.region].filter(Boolean).join(' · ') || 'Riwayat PM & CM'}
            </small>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              className="btn btn-secondary"
              onClick={generatePDF}
              disabled={!data}
              title="Cetak riwayat armada ini ke PDF"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: '0.78rem', background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff' }}
            >
              <FileText size={15} /><span>Export PDF</span>
            </button>
            <button
              className="btn btn-secondary"
              onClick={exportCSV}
              disabled={!data}
              title="Unduh riwayat armada ini sebagai CSV"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: '0.78rem', background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff' }}
            >
              <FileSpreadsheet size={15} /><span>Export CSV</span>
            </button>
            <button className="btn-icon" onClick={onClose} style={{ color: '#fff' }} aria-label="Tutup"><X size={20} /></button>
          </div>
        </div>

        <div className="modal-body" style={{ padding: '4px 20px 24px', background: '#fff' }}>
          {loading && <div className="page-loading"><div className="spinner-lg" /><p>Memuat perjalanan armada...</p></div>}
          {!loading && error && (
            <div style={{ padding: 24, color: '#f5365c', fontWeight: 600 }}>{error}</div>
          )}
          {!loading && !error && data && (
            <>
              {/* IDENTITAS + DOKUMEN */}
              <JourneySection icon={Truck} title="Identitas & Dokumen" subtitle="Data master armada dan masa berlaku dokumen" color="#11cdef">
                <table className="identity" style={{ width: '100%', fontSize: '0.82rem', marginBottom: 10 }}>
                  <tbody>
                    <tr><td style={{ fontWeight: 600, width: 130 }}>Nama Klien</td><td>: {info.operator || '-'}</td><td style={{ fontWeight: 600, width: 130 }}>No. Kontrak</td><td>: {info.contract_no || '-'}</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>No. Polisi</td><td>: <strong>{info.plate_no || '-'}</strong></td><td style={{ fontWeight: 600 }}>No. Body</td><td>: {info.body_no || '-'}</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>Rute</td><td>: {info.route_code || '-'}</td><td style={{ fontWeight: 600 }}>Wilayah</td><td>: {info.region || '-'}</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>Jenis Armada</td><td>: {info.type || '-'}</td><td style={{ fontWeight: 600 }}>AC / Non AC</td><td>: {info.jenis_ac || '-'}</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>Merek / Tahun</td><td>: {[info.brand, info.tahun].filter(Boolean).join(' / ') || '-'}</td><td style={{ fontWeight: 600 }}>Teknisi Armada</td><td>: {info.teknisi || '-'}</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>Pemilik</td><td>: {info.owner_name || '-'}</td><td style={{ fontWeight: 600 }}>Pengendapan</td><td>: {info.pengendapan || '-'}</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>No. Chassis</td><td>: {info.chassis_no || '-'}</td><td style={{ fontWeight: 600 }}>No. Mesin</td><td>: {info.engine_no || '-'}</td></tr>
                    <tr><td style={{ fontWeight: 600 }}>Status Armada</td><td>: <StatusBadge status={info.is_active ? 'Aktif' : 'Non-Aktif'} /></td><td style={{ fontWeight: 600 }}>Hari Sejak Servis</td><td>: {sum.hari_sejak_servis > 0 ? `${sum.hari_sejak_servis} hari` : '-'}</td></tr>
                  </tbody>
                </table>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {[
                    { label: 'STNK', status: info.stnk, until: info.stnk_until },
                    { label: 'KIR', status: info.kir, until: info.kir_until },
                    { label: 'KP', status: info.kp, until: info.kp_until },
                    { label: 'Asuransi', status: info.insurance_status, until: info.insurance_until },
                  ].map(d => (
                    <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #eef0f4', borderRadius: 8, padding: '6px 10px', fontSize: '0.76rem' }}>
                      <span style={{ fontWeight: 700, color: docTone(d.status) }}>{d.label}</span>
                      <span className="badge" style={{ background: docTone(d.status) + '1a', color: docTone(d.status) }}>{d.status || 'Tidak ada data'}</span>
                      <span style={{ color: 'var(--gray-400)' }}>{d.until ? `s.d. ${d.until}` : ''}</span>
                    </div>
                  ))}
                </div>
              </JourneySection>

              {/* RINGKASAN */}
              <JourneySection icon={Activity} title="Ringkasan Perjalanan" subtitle={sum.periode_awal ? `Aktivitas ${sum.periode_awal} s.d. ${sum.periode_akhir}` : 'Belum ada aktivitas tercatat'} color="#2dce89">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                  <JourneyStat label="Total Aktivitas" value={sum.total_aktivitas || 0} sub={`PM ${sum.total_pm || 0} · CM ${sum.total_cm || 0}`} color="#11cdef" />
                  <JourneyStat label="Selesai" value={sum.total_aktivitas ? `${sum.pm_selesai + sum.cm_selesai}/${sum.total_aktivitas}` : '0'} sub={`PM ${sum.pm_selesai || 0} · CM ${sum.cm_selesai || 0}`} color="#2dce89" />
                  <JourneyStat label="Sparepart Terpakai" value={fmtQty(sum.total_sparepart_qty || 0)} sub={`${sum.jumlah_sparepart || 0} jenis · ${fmtRp(sum.total_sparepart_nilai || 0)}`} color="#8965e0" />
                  <JourneyStat label="Jasa" value={sum.total_jasa_item || 0} sub="item jasa dipakai" color="#fb6340" />
                  <JourneyStat label="Total Biaya CM" value={fmtRp(sum.total_nominal || 0)} sub="nominal tagihan" color="#f5365c" />
                  <JourneyStat label="Teknisi Terlibat" value={sum.jumlah_teknisi || 0} sub={`${sum.jumlah_klien || 0} klien`} color="#0d7a8a" />
                  <JourneyStat label="Odometer Terakhir" value={sum.odometer_terakhir ? `${sum.odometer_terakhir.toLocaleString('id-ID')} km` : '-'} sub={`${sum.jumlah_foto || 0} foto bukti`} color="#11cdef" />
                  <JourneyStat label="Umur Armada" value={sum.umur_hari > 0 ? `${sum.umur_hari.toLocaleString('id-ID')} hari` : '-'} sub={info.start_date ? `mulai ${info.start_date}` : 'tanggal mulai belum diisi'} color="#8898aa" />
                </div>
              </JourneySection>

              {/* SPAREPART AGREGAT */}
              <JourneySection icon={Package} title="Sparepart yang Pernah Dipakai" subtitle={`${(data.spareparts || []).length} jenis`} color="#8965e0">
                {(data.spareparts || []).length === 0 ? (
                  <p className="text-muted text-sm">Belum ada pemakaian sparepart yang tercatat untuk armada ini.</p>
                ) : (
                  <div className="table-wrapper" style={{ maxHeight: 280, overflow: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr><th>Nama Sparepart</th><th>Qty</th><th>Harga</th><th>Total</th><th>Dipakai</th><th>Terakhir</th></tr>
                      </thead>
                      <tbody>
                        {data.spareparts.map((s, i) => (
                          <tr key={i}>
                            <td><strong>{s.nama}</strong></td>
                            <td>{fmtQty(s.qty)}</td>
                            <td>{fmtRp(s.harga)}</td>
                            <td><strong>{fmtRp(s.nilai)}</strong></td>
                            <td>{s.transaksi}x</td>
                            <td>{s.terakhir || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </JourneySection>

              {/* TEKNISI + PER BULAN */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                <JourneySection icon={User} title="Teknisi yang Menangani" subtitle={`${(data.by_teknisi || []).length} teknisi`} color="#fb6340">
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead><tr><th>Nama Teknisi</th><th>PM</th><th>CM</th><th>Pendamping</th><th>Total</th><th>Terakhir</th></tr></thead>
                      <tbody>
                        {(data.by_teknisi || []).map((t, i) => (
                          <tr key={i}>
                            <td><strong>{t.nama}</strong></td>
                            <td>{t.pm}</td><td>{t.cm}</td>
                            <td>{t.pendamping || '-'}</td>
                            <td><strong>{t.total}</strong></td>
                            <td>{t.terakhir || '-'}</td>
                          </tr>
                        ))}
                        {(data.by_teknisi || []).length === 0 && (
                          <tr><td colSpan={6} className="text-center text-muted" style={{ padding: 20 }}>Belum ada data teknisi.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </JourneySection>

                <JourneySection icon={BarChart3} title="Aktivitas per Bulan" subtitle="PM vs CM" color="#2dce89">
                  {(data.by_bulan || []).length === 0 ? (
                    <p className="text-muted text-sm">Belum ada aktivitas.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {data.by_bulan.map((b, i) => {
                        const max = Math.max(...data.by_bulan.map(x => x.total), 1);
                        return (
                          <div key={i}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                              <span>{b.label}</span>
                              <span>PM {b.pm} · CM {b.cm} · total {b.total}</span>
                            </div>
                            <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#eef0f4', marginTop: 3 }}>
                              <div style={{ width: `${(b.pm / max) * 100}%`, background: '#11cdef' }} />
                              <div style={{ width: `${(b.cm / max) * 100}%`, background: '#fb6340' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </JourneySection>
              </div>

              {/* TIMELINE */}
              <JourneySection icon={History} title="Riwayat Aktivitas (Timeline)" subtitle={`${timeline.length} dari ${(data.timeline || []).length} aktivitas — klik baris untuk rincian sparepart & jasa`} color="#0d7a8a">
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  {[{ k: 'semua', l: 'Semua' }, { k: 'PM', l: `PM (${sum.total_pm || 0})` }, { k: 'CM', l: `CM (${sum.total_cm || 0})` }].map(f => (
                    <button
                      key={f.k}
                      onClick={() => setTimelineFilter(f.k)}
                      className={`btn ${timelineFilter === f.k ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ padding: '5px 12px', fontSize: '0.75rem' }}
                    >{f.l}</button>
                  ))}
                </div>
                {timeline.length === 0 ? (
                  <p className="text-muted text-sm">Belum ada aktivitas PM/CM untuk armada ini.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {timeline.map(it => {
                      const key = `${it.tipe}-${it.id}`;
                      const open = !!openItems[key];
                      const isPM = it.tipe === 'PM';
                      const tone = isPM ? '#11cdef' : '#fb6340';
                      return (
                        <div key={key} className="journey-timeline-item" style={{ border: '1px solid #eef0f4', borderLeft: `4px solid ${tone}`, borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                          <div
                            onClick={() => toggleItem(key, it)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', flexWrap: 'wrap' }}
                          >
                            <span className="badge" style={{ background: tone + '1a', color: tone, fontWeight: 700 }}>{it.tipe}</span>
                            <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>{it.tanggal || '-'}</span>
                            <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)', flex: '1 1 200px' }}>{it.jenis || it.no_ref}</span>
                            <StatusBadge status={it.status} />
                            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}><User size={12} style={{ verticalAlign: '-2px' }} /> {it.teknisi || '-'}</span>
                            {it.qty_sparepart > 0 && (
                              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#8965e0' }}>
                                {fmtQty(it.qty_sparepart)} SP · {fmtRp(it.nilai_sparepart)}
                              </span>
                            )}
                            {it.nominal > 0 && <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#f5365c' }}>{fmtRp(it.nominal)}</span>}
                            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </div>

                          {open && (
                            <div style={{ padding: '10px 12px 14px', background: '#fafbfc', borderTop: '1px solid #eef0f4' }}>
                              <table className="identity" style={{ width: '100%', fontSize: '0.78rem', marginBottom: 10 }}>
                                <tbody>
                                  <tr><td style={{ fontWeight: 600, width: 130 }}>No. Referensi</td><td>: {it.no_ref}</td><td style={{ fontWeight: 600, width: 130 }}>Tanggal Selesai</td><td>: {it.tanggal_selesai || '-'}</td></tr>
                                  <tr><td style={{ fontWeight: 600 }}>Jenis Pekerjaan</td><td>: {it.jenis || '-'}</td><td style={{ fontWeight: 600 }}>Tipe Pekerjaan</td><td>: {it.tipe_pekerjaan || '-'}</td></tr>
                                  <tr><td style={{ fontWeight: 600 }}>Teknisi</td><td>: {it.teknisi || '-'}{it.teknisi_pendamping ? ` (+ ${it.teknisi_pendamping})` : ''}</td><td style={{ fontWeight: 600 }}>Odometer</td><td>: {it.odometer ? `${it.odometer.toLocaleString('id-ID')} km` : '-'}</td></tr>
                                  <tr><td style={{ fontWeight: 600 }}>Klien</td><td>: {it.klien || '-'}</td><td style={{ fontWeight: 600 }}>Lokasi</td><td>: {it.lokasi || '-'}</td></tr>
                                  <tr><td style={{ fontWeight: 600 }}>Pengawas</td><td>: {it.pengawas || '-'}</td><td style={{ fontWeight: 600 }}>Disetujui Oleh</td><td>: {it.disetujui || '-'}</td></tr>
                                  {isPM && <tr><td style={{ fontWeight: 600 }}>Checklist</td><td>: {it.checklist_ok || 0}/{it.jumlah_checklist || 0} item OK</td><td style={{ fontWeight: 600 }}>Foto</td><td>: {it.jumlah_foto || 0}</td></tr>}
                                  {!isPM && <tr><td style={{ fontWeight: 600 }}>Foto Bukti</td><td>: {it.jumlah_foto || 0}</td><td style={{ fontWeight: 600 }}>Nominal</td><td>: {fmtRp(it.nominal || 0)}</td></tr>}
                                  {it.detail_pekerjaan && <tr><td style={{ fontWeight: 600 }}>Detail</td><td colSpan={3}>: {it.detail_pekerjaan}</td></tr>}
                                  {it.catatan && <tr><td style={{ fontWeight: 600 }}>Catatan</td><td colSpan={3}>: {it.catatan}</td></tr>}
                                </tbody>
                              </table>

                              {it.spareparts.length > 0 && (
                                <>
                                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#8965e0', margin: '8px 0 4px' }}>
                                    Sparepart ({it.spareparts.length} item · {fmtQty(it.qty_sparepart)} qty · {fmtRp(it.nilai_sparepart)})
                                  </div>
                                  <table className="data-table" style={{ fontSize: '0.78rem' }}>
                                    <thead><tr><th>Nama</th><th>Qty</th><th>Harga</th><th>Subtotal</th></tr></thead>
                                    <tbody>
                                      {it.spareparts.map((p, pi) => (
                                        <tr key={pi}><td>{p.nama}</td><td>{fmtQty(p.jumlah)}</td><td>{fmtRp(p.harga)}</td><td>{fmtRp(p.subtotal)}</td></tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </>
                              )}

                              {it.jasa.length > 0 && (
                                <>
                                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#fb6340', margin: '10px 0 4px' }}>Jasa ({it.jasa.length} item)</div>
                                  <table className="data-table" style={{ fontSize: '0.78rem' }}>
                                    <thead><tr><th>Nama Jasa</th><th>Qty</th><th>Harga</th><th>Subtotal</th></tr></thead>
                                    <tbody>
                                      {it.jasa.map((p, pi) => (
                                        <tr key={pi}><td>{p.nama}</td><td>{fmtQty(p.jumlah)}</td><td>{fmtRp(p.harga)}</td><td>{fmtRp(p.subtotal)}</td></tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </>
                              )}

                              {it.spareparts.length === 0 && it.jasa.length === 0 && (
                                <p className="text-muted text-sm" style={{ margin: 0 }}>
                                  Tidak ada sparepart/jasa yang dicatat pada aktivitas ini.
                                </p>
                              )}

                              {/* FOTO BUKTI — dimuat hanya saat baris ini dibuka */}
                              {(it.jumlah_foto || 0) > 0 && (
                                <div style={{ marginTop: 12 }}>
                                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#0d7a8a', marginBottom: 6 }}>
                                    Foto Bukti ({it.jumlah_foto})
                                  </div>
                                  {(() => {
                                    const st = photoState[key];
                                    if (!st) return <p className="text-muted text-sm" style={{ margin: 0 }}>Menyiapkan foto...</p>;
                                    return (
                                      <>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                          {st.urls.map(f => f.error ? (
                                            <div key={f.idx} style={{ width: 84, height: 84, borderRadius: 8, border: '1px dashed #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', color: '#8898aa', textAlign: 'center', padding: 4 }}>
                                              Foto {(f.idx + 1)} gagal dimuat
                                            </div>
                                          ) : (
                                            <img
                                              key={f.idx}
                                              src={f.url}
                                              alt={`Foto ${f.idx + 1} ${it.tipe} ${it.no_ref}`}
                                              onClick={() => setLightbox({ url: f.url, label: `${it.tipe} ${it.no_ref} — foto ${f.idx + 1}` })}
                                              style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid #eef0f4', cursor: 'zoom-in', background: '#f8f9fa' }}
                                            />
                                          ))}
                                        </div>
                                        {st.status === 'loading' && (
                                          <p className="text-muted text-sm" style={{ margin: '6px 0 0' }}>Memuat foto... ({st.urls.filter(f => !f.error).length}/{it.jumlah_foto})</p>
                                        )}
                                        <p style={{ margin: '6px 0 0', fontSize: '0.66rem', color: '#8898aa' }}>
                                          Klik foto untuk memperbesar. Foto disimpan apa adanya di server (belum dikompres), jadi bisa berukuran besar.
                                        </p>
                                      </>
                                    );
                                  })()}
                                </div>
                              )}

                              {/* VIDEO BUKTI — hanya tautan, filenya sudah berupa URL */}
                              {(it.video_urls || []).length > 0 && (
                                <div style={{ marginTop: 10 }}>
                                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: '#fb6340', marginBottom: 4 }}>
                                    Video Bukti ({it.video_urls.length})
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {it.video_urls.map((u, vi) => (
                                      <a
                                        key={vi}
                                        href={u}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 8, background: '#fff7ed', color: '#fb6340', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'none', border: '1px solid #fed7aa' }}
                                      >
                                        ▶ Lihat video {vi + 1}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </JourneySection>

              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                <ClipboardCheck size={13} />
                Data diambil langsung dari riwayat PM & CM armada ini (seluruh periode).
              </div>
            </>
          )}
        </div>
      </div>

      {/* Pratinjau foto ukuran besar.
          Dibuat lewat portal ke <body> supaya benar-benar menutupi seluruh layar:
          modal ini berada di dalam .dash-content (stacking context z-index 2), jadi
          tanpa portal, sidebar (z-index 100) akan tampak menimpa pratinjau di sisi kiri. */}
      {lightbox && createPortal(
        <div
          /* stopPropagation penting: portal tetap meneruskan event ke pohon React,
             jadi tanpa ini klik pada pratinjau ikut menutup modal perjalanannya. */
          onClick={e => { e.stopPropagation(); setLightbox(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 3000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div style={{ color: '#fff', fontSize: '0.8rem', marginBottom: 10, textAlign: 'center' }}>{lightbox.label}</div>
          <img src={lightbox.url} alt={lightbox.label} style={{ maxWidth: '92vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }} />
          <button className="btn btn-secondary" onClick={e => { e.stopPropagation(); setLightbox(null); }} style={{ marginTop: 12, padding: '6px 16px', fontSize: '0.8rem' }}>
            Tutup (klik di mana saja)
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

// =============================================
// TAB: SPAREPART
// =============================================
function TabSparepart({ data }) {
  const [search, setSearch] = useState('');

  const generatePDF = () => {
    if (!data) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;

    pdfHeader(doc, 'LAPORAN SPAREPART', `Total: ${data.total} item | Nilai: Rp ${(data.total_value / 1000000).toFixed(1)}jt`, pageW);

    const startY = 48;
    doc.setDrawColor(230, 230, 230);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 28, 3, 3, 'FD');
    const statW = (pageW - 2 * margin - 15) / 3;
    pdfStatBox(doc, margin + 5, startY, statW, 'Total Stok', data.total_stock, [45, 206, 137]);
    pdfStatBox(doc, margin + statW + 10, startY, statW, 'Stok Rendah', data.low_stock, [251, 99, 64]);
    pdfStatBox(doc, margin + 2 * (statW + 5) + 5, startY, statW, 'Stok Habis', data.by_status.find(s => s.status === 'Stok Habis')?.count || 0, [245, 54, 92]);

    const headers = [['No', 'Kode', 'Nama', 'Unit', 'Harga', 'Stok', 'Nilai']];
    const rows = data.data.map((s, i) => [
      (i + 1).toString(), s.code || '-', s.name || '-', s.unit || '-',
      `Rp ${(s.price || 0).toLocaleString('id-ID')}`,
      (s.stock || 0).toString(),
      `Rp ${((s.price || 0) * (s.stock || 0)).toLocaleString('id-ID')}`,
    ]);

    autoTable(doc, {
      ...pdfAutoTableOpts(startY + 40, margin),
      head: headers,
      body: rows,
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        4: { halign: 'right' },
        5: { halign: 'center' },
        6: { halign: 'right' },
      },
    });

    pdfFooter(doc, pageW);
    doc.save('Laporan_Sparepart.pdf');
  };

  if (!data) return <div className="page-loading"><div className="spinner-lg" /><p>Memuat data...</p></div>;

  const filtered = data.data.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.name || '').toLowerCase().includes(q) || (s.code || '').toLowerCase().includes(q);
  });

  // Stock donut data
  const stockPie = data.by_status.map(s => ({
    name: s.status,
    value: s.count,
    color: s.status === 'Stok Habis' ? '#f5365c' : s.status === 'Stok Rendah (1-5)' ? '#fb6340' : '#2dce89'
  }));

  return (
    <div>
      <div className="dash-stats-row" style={{ marginBottom: 20 }}>
        <div className="dash-stat-col">
          <StatCardMini icon={Package} label="Total Item" value={data.total} color="#11cdef" sublabel="Sparepart terdaftar" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={Package} label="Total Stok" value={data.total_stock} color="#2dce89" sublabel="Unit tersedia" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={DollarSign} label="Nilai Inventaris" value={`Rp ${(data.total_value / 1000000).toFixed(1)}jt`} color="#11cdef" sublabel="Harga × Stok" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={AlertTriangle} label="Stok Rendah" value={data.low_stock} color="#fb6340" sublabel="Perlu restock" />
        </div>
      </div>

      <div className="dash-charts-row" style={{ marginBottom: 20 }}>
        {/* Stock Status Donut */}
        <div className="dash-chart-col-4">
          <ChartCard title="Status Stok" subtitle="Habis, rendah, tersedia">
            {stockPie.length > 0 ? (
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={stockPie} cx="50%" cy="50%"
                      innerRadius={55} outerRadius={85}
                      paddingAngle={3} dataKey="value"
                    >
                      {stockPie.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-legend">
                  {stockPie.map((d, i) => (
                    <div key={i} className="donut-legend-item">
                      <span className="donut-dot" style={{ background: d.color }} />
                      <span>{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>

        {/* Price Ranges Bar */}
        <div className="dash-chart-col-4">
          <ChartCard title="Sebaran Harga" subtitle="Per rentang harga">
            {data.price_ranges.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.price_ranges} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                  <XAxis dataKey="range" stroke="#8898aa" fontSize={9} angle={-20} textAnchor="end" height={60} />
                  <YAxis stroke="#8898aa" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={28}>
                    {data.price_ranges.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>

        {/* Ciri-ciri */}
        <div className="dash-chart-col-4">
          <ChartCard title="Ringkasan" subtitle="Status inventaris">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '10px 0' }}>
              {data.by_status.map((s, i) => {
                const pct = data.total > 0 ? Math.round((s.count / data.total) * 100) : 0;
                const colors = ['#f5365c', '#fb6340', '#2dce89'];
                return (
                  <div key={i} className="stat-progress-item">
                    <div className="stat-progress-header">
                      <span className="stat-progress-label">{s.status}</span>
                      <span className="stat-progress-value">{s.count} item ({pct}%)</span>
                    </div>
                    <div className="stat-progress-bar">
                      <div className="stat-progress-fill" style={{ width: `${pct}%`, background: colors[i] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Table */}
      <div className="page-header-row" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          Data Sparepart ({data.total})
        </h3>
        <div className="page-header-actions">
          <div className="search-box">
            <Search size={16} />
            <input placeholder="Cari nama atau kode..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-primary" onClick={generatePDF} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={16} /><span>Export PDF</span>
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Kode</th>
              <th>Nama</th>
              <th>Unit</th>
              <th>Harga</th>
              <th>Stok</th>
              <th>Nilai</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => (
              <tr key={s.id}>
                <td><code>{s.code || '-'}</code></td>
                <td><strong>{s.name}</strong></td>
                <td>{s.unit || '-'}</td>
                <td>Rp {(s.price || 0).toLocaleString('id-ID')}</td>
                <td>
                  <span style={{
                    display: 'inline-flex', padding: '2px 10px', borderRadius: 20,
                    fontSize: '0.75rem', fontWeight: 600,
                    background: s.stock <= 0 ? '#fee2e2' : s.stock <= 5 ? '#fef3c7' : '#d1fae5',
                    color: s.stock <= 0 ? '#991b1b' : s.stock <= 5 ? '#92400e' : '#065f46',
                  }}>
                    {s.stock || 0}
                  </span>
                </td>
                <td>Rp {((s.price || 0) * (s.stock || 0)).toLocaleString('id-ID')}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted" style={{ padding: 40 }}>Tidak ada data sparepart.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================
// TAB: MAINTENANCE
// =============================================
function TabMaintenance({ data }) {
  const [activeSubTab, setActiveSubTab] = useState('pm');

  const generatePDF = () => {
    if (!data) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;

    pdfHeader(doc, 'LAPORAN MAINTENANCE', `PM: ${data.total_pm} | CM: ${data.total_cm} | Biaya: Rp ${(data.total_biaya / 1000000).toFixed(1)}jt`, pageW);

    const startY = 48;
    doc.setDrawColor(230, 230, 230);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 28, 3, 3, 'FD');
    const statW = (pageW - 2 * margin - 15) / 3;
    pdfStatBox(doc, margin + 5, startY, statW, 'Total PM', data.total_pm, [17, 205, 239]);
    pdfStatBox(doc, margin + statW + 10, startY, statW, 'Total CM', data.total_cm, [251, 99, 64]);
    pdfStatBox(doc, margin + 2 * (statW + 5) + 5, startY, statW, 'Biaya CM', `Rp ${(data.total_biaya / 1000000).toFixed(1)}jt`, [245, 54, 92]);

    // PM Table
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('Preventive Maintenance (PM)', margin, startY + 42);

    const pmHeaders = [['No', 'No. Polisi', 'Jenis Pemeriksaan', 'Teknisi', 'Tanggal', 'Status']];
    const pmRows = data.recent_pm.map((p, i) => [
      (i + 1).toString(), p.no_polisi || '-', p.jenis_pemeriksaan || '-', p.teknisi || '-', p.tanggal || '-', p.status || '-',
    ]);

    autoTable(doc, {
      ...pdfAutoTableOpts(startY + 48, margin),
      head: pmHeaders,
      body: pmRows,
      columnStyles: { 0: { halign: 'center', cellWidth: 12 }, 1: { fontStyle: 'bold' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
    });

    // CM Table
    const cmStartY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('Corrective Maintenance (CM)', margin, cmStartY);

    const cmHeaders = [['No', 'No. Polisi', 'Jenis Pekerjaan', 'Teknisi', 'Tanggal', 'Biaya', 'Status']];
    const cmRows = data.recent_cm.map((c, i) => [
      (i + 1).toString(), c.no_polisi || '-', c.jenis_pekerjaan || '-', c.nama_teknisi || '-', c.tanggal || '-',
      `Rp ${(c.nominal_tagihan || 0).toLocaleString('id-ID')}`, c.status || '-',
    ]);

    autoTable(doc, {
      ...pdfAutoTableOpts(cmStartY + 6, margin),
      head: cmHeaders,
      body: cmRows,
      columnStyles: { 0: { halign: 'center', cellWidth: 12 }, 1: { fontStyle: 'bold' }, 4: { halign: 'center' }, 5: { halign: 'right' }, 6: { halign: 'center' } },
    });

    pdfFooter(doc, pageW);
    doc.save('Laporan_Maintenance.pdf');
  };

  if (!data) return <div className="page-loading"><div className="spinner-lg" /><p>Memuat data...</p></div>;

  // Combine PM + CM monthly data for chart
  const monthlyData = data.pm_by_month.map((pm, i) => ({
    bulan: pm.bulan,
    PM: pm.count,
    CM: data.cm_by_month[i]?.count || 0,
  }));

  const pmStatusPie = data.pm_by_status.map((s, i) => ({
    name: s.status, value: s.count, color: COLORS[i % COLORS.length]
  }));

  const cmStatusPie = data.cm_by_status.map((s, i) => ({
    name: s.status, value: s.count, color: COLORS[i % COLORS.length]
  }));

  return (
    <div>
      <div className="dash-stats-row" style={{ marginBottom: 20 }}>
        <div className="dash-stat-col">
          <StatCardMini icon={Wrench} label="Total PM" value={data.total_pm} color="#11cdef" sublabel="Preventive Maintenance" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={Wrench} label="Total CM" value={data.total_cm} color="#fb6340" sublabel="Corrective Maintenance" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={DollarSign} label="Biaya CM" value={`Rp ${(data.total_biaya / 1000000).toFixed(1)}jt`} color="#f5365c" sublabel="Total tagihan" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={AlertTriangle} label="Total Laporan" value={data.total_pm + data.total_cm} color="#11cdef" sublabel="Semua kegiatan" />
        </div>
      </div>

      {/* Monthly Trend */}
      <div className="dash-charts-row" style={{ marginBottom: 20 }}>
        <div className="dash-chart-col-6">
          <ChartCard title="Tren Bulanan" subtitle="Jumlah PM & CM per bulan (6 bulan terakhir)">
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                  <XAxis dataKey="bulan" stroke="#8898aa" fontSize={12} />
                  <YAxis stroke="#8898aa" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="PM" stroke="#11cdef" strokeWidth={2} dot={{ fill: '#11cdef', r: 4 }} />
                  <Line type="monotone" dataKey="CM" stroke="#fb6340" strokeWidth={2} dot={{ fill: '#fb6340', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>

        {/* Status Donuts */}
        <div className="dash-chart-col-3">
          <ChartCard title="Status PM" subtitle="Preventive">
            {pmStatusPie.length > 0 ? (
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={pmStatusPie} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                      {pmStatusPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-legend" style={{ flexDirection: 'column', alignItems: 'center' }}>
                  {pmStatusPie.map((d, i) => (
                    <div key={i} className="donut-legend-item">
                      <span className="donut-dot" style={{ background: d.color }} />
                      <span>{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>

        <div className="dash-chart-col-3">
          <ChartCard title="Status CM" subtitle="Corrective">
            {cmStatusPie.length > 0 ? (
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={cmStatusPie} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                      {cmStatusPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-legend" style={{ flexDirection: 'column', alignItems: 'center' }}>
                  {cmStatusPie.map((d, i) => (
                    <div key={i} className="donut-legend-item">
                      <span className="donut-dot" style={{ background: d.color }} />
                      <span>{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>
      </div>

      {/* Sub Tab: PM / CM */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setActiveSubTab('pm')}
          className={`btn ${activeSubTab === 'pm' ? 'btn-primary' : 'btn-secondary'}`}
        >
          PM Terbaru
        </button>
        <button
          onClick={() => setActiveSubTab('cm')}
          className={`btn ${activeSubTab === 'cm' ? 'btn-primary' : 'btn-secondary'}`}
        >
          CM Terbaru
        </button>
        <button className="btn btn-primary" onClick={generatePDF} style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <FileText size={16} /><span>Export PDF</span>
        </button>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {activeSubTab === 'pm' ? (
                <>
                  <th>No. Polisi</th>
                  <th>Jenis Pemeriksaan</th>
                  <th>Teknisi</th>
                  <th>Tanggal</th>
                  <th>Status</th>
                </>
              ) : (
                <>
                  <th>No. Polisi</th>
                  <th>Jenis Pekerjaan</th>
                  <th>Teknisi</th>
                  <th>Tanggal</th>
                  <th>Biaya</th>
                  <th>Status</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {(activeSubTab === 'pm' ? data.recent_pm : data.recent_cm).map((item, i) => (
              <tr key={i}>
                {activeSubTab === 'pm' ? (
                  <>
                    <td><strong>{item.no_polisi || '-'}</strong></td>
                    <td>{item.jenis_pemeriksaan || '-'}</td>
                    <td>{item.teknisi || '-'}</td>
                    <td>{item.tanggal || '-'}</td>
                    <td><StatusBadge status={item.status} /></td>
                  </>
                ) : (
                  <>
                    <td><strong>{item.no_polisi || '-'}</strong></td>
                    <td>{item.jenis_pekerjaan || '-'}</td>
                    <td>{item.nama_teknisi || '-'}</td>
                    <td>{item.tanggal || '-'}</td>
                    <td>Rp {(item.nominal_tagihan || 0).toLocaleString('id-ID')}</td>
                    <td><StatusBadge status={item.status} /></td>
                  </>
                )}
              </tr>
            ))}
            {(activeSubTab === 'pm' ? data.recent_pm : data.recent_cm).length === 0 && (
              <tr><td colSpan={activeSubTab === 'pm' ? 5 : 6} className="text-center text-muted" style={{ padding: 40 }}>
                Belum ada data {activeSubTab === 'pm' ? 'PM' : 'CM'}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================
// TAB: ABSENSI
// =============================================
function TabAbsensi({ data, onDateFilter, activeFilters }) {
  const [dateStart, setDateStart] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [dateEnd, setDateEnd] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });

  const generatePDF = () => {
    if (!data) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;

    const dateLabel = activeFilters?.tanggal_start
      ? `${activeFilters.tanggal_start} s.d ${activeFilters.tanggal_end}`
      : 'Hari Ini';
    pdfHeader(doc, 'LAPORAN ABSENSI', `Periode: ${dateLabel} | Karyawan Aktif: ${data.karyawan_aktif}`, pageW);

    const startY = 48;
    doc.setDrawColor(230, 230, 230);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 28, 3, 3, 'FD');
    const statW = (pageW - 2 * margin - 15) / 3;
    pdfStatBox(doc, margin + 5, startY, statW, 'Hadir', data.hari_ini?.hadir || 0, [45, 206, 137]);
    pdfStatBox(doc, margin + statW + 10, startY, statW, 'Karyawan Aktif', data.karyawan_aktif, [17, 205, 239]);
    pdfStatBox(doc, margin + 2 * (statW + 5) + 5, startY, statW, 'Total Records', data.total_records, [251, 99, 64]);

    const headers = [['No', 'Tanggal', 'NIK', 'Nama', 'Check-in', 'Check-out', 'Status']];
    const rows = (data.recent_data || []).map((d, i) => [
      (i + 1).toString(), d.tanggal || '-', d.nik || '-', d.nama || '-',
      d.check_in || '-', d.check_out || '-', d.status || '-',
    ]);

    autoTable(doc, {
      ...pdfAutoTableOpts(startY + 40, margin),
      head: headers,
      body: rows,
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        3: { fontStyle: 'bold' },
        4: { halign: 'center' },
        5: { halign: 'center' },
        6: { halign: 'center' },
      },
    });

    pdfFooter(doc, pageW);
    doc.save('Laporan_Absensi.pdf');
  };

  if (!data) return <div className="page-loading"><div className="spinner-lg" /><p>Memuat data...</p></div>;

  const monthlyData = data.by_month || [];
  const bulanPie = data.bulan_ini?.map((s, i) => ({
    name: s.status,
    value: s.count,
    color: ['#2dce89', '#11cdef', '#fb6340', '#f5365c', '#14b8c4'][i % 5]
  })) || [];

  const todayStr = () => new Date().toISOString().slice(0, 10);

  const handleApplyFilter = () => {
    if (onDateFilter) {
      onDateFilter({ tanggal_start: dateStart, tanggal_end: dateEnd });
    }
  };

  return (
    <div>
      {/* Date Range Filter */}
      <div className="filters" style={{ marginBottom: 20 }}>
        <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
          <label style={{ fontSize: '0.75rem', marginBottom: 4, color: 'var(--gray-400)' }}>Tanggal Mulai</label>
          <input
            type="date"
            value={dateStart}
            onChange={e => setDateStart(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }}
          />
        </div>
        <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
          <label style={{ fontSize: '0.75rem', marginBottom: 4, color: 'var(--gray-400)' }}>Tanggal Akhir</label>
          <input
            type="date"
            value={dateEnd}
            onChange={e => setDateEnd(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }}
          />
        </div>
        <button
          className="btn btn-primary"
          onClick={handleApplyFilter}
          style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Filter size={16} /> Terapkan Filter
        </button>
        {activeFilters?.tanggal_start && (
          <button
            className="btn btn-secondary"
            onClick={() => {
              setDateStart(todayStr());
              setDateEnd(todayStr());
              if (onDateFilter) onDateFilter({});
            }}
            style={{ marginTop: 22 }}
          >
            Reset
          </button>
        )}
      </div>

      <div className="dash-stats-row" style={{ marginBottom: 20 }}>
        <div className="dash-stat-col">
          <StatCardMini icon={Clock} label="Hadir" value={data.hari_ini?.hadir} color="#2dce89"
            sublabel={`Dari ${data.hari_ini?.total || 0} total absen`} />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={LogIn} label="Check-in" value={data.hari_ini?.checkin} color="#11cdef"
            sublabel={`${data.hari_ini?.checkout || 0} sudah check-out`} />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={User} label="Karyawan Aktif" value={data.karyawan_aktif} color="#11cdef"
            sublabel={`${data.total_records} total records`} />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={AlertTriangle} label="Alpha/Izin/Sakit" value={(data.hari_ini?.alpha || 0) + (data.hari_ini?.izin || 0) + (data.hari_ini?.sakit || 0)} color="#fb6340"
            sublabel={`Alpha: ${data.hari_ini?.alpha || 0}, Izin: ${data.hari_ini?.izin || 0}, Sakit: ${data.hari_ini?.sakit || 0}`} />
        </div>
      </div>

      <div className="dash-charts-row" style={{ marginBottom: 20 }}>
        {/* Monthly Trend */}
        <div className="dash-chart-col-6">
          <ChartCard title="Tren Absensi Bulanan" subtitle={activeFilters?.tanggal_start ? `${dateStart} s.d ${dateEnd}` : '6 bulan terakhir'}>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                  <XAxis dataKey="bulan" stroke="#8898aa" fontSize={12} />
                  <YAxis stroke="#8898aa" fontSize={11} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={32} fill="#11cdef" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>

        {/* Status Donut */}
        <div className="dash-chart-col-6">
          <ChartCard title="Status" subtitle={activeFilters?.tanggal_start ? `${dateStart} s.d ${dateEnd}` : 'Bulan Ini'}>
            {bulanPie.length > 0 ? (
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={bulanPie} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                      {bulanPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-legend">
                  {bulanPie.map((d, i) => (
                    <div key={i} className="donut-legend-item">
                      <span className="donut-dot" style={{ background: d.color }} />
                      <span>{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-muted text-sm">Belum ada data absensi.</p>}
          </ChartCard>
        </div>
      </div>

      {/* Recent Table */}
      <div className="page-header-row" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          Data Absensi ({data.recent_data?.length || 0})
        </h3>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={generatePDF} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={16} /><span>Export PDF</span>
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Tanggal</th><th>NIK</th><th>Nama</th><th>Check-in</th><th>Check-out</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(data.recent_data || []).map((d, i) => (
              <tr key={i}>
                <td>{d.tanggal}</td>
                <td><code>{d.nik || '-'}</code></td>
                <td><strong>{d.nama}</strong></td>
                <td>{d.check_in ? <span style={{ color: '#2dce89', fontWeight: 600 }}>{d.check_in}</span> : '-'}</td>
                <td>{d.check_out ? <span style={{ color: '#fb6340', fontWeight: 600 }}>{d.check_out}</span> : '-'}</td>
                <td><StatusBadge status={d.status} /></td>
              </tr>
            ))}
            {(!data.recent_data || data.recent_data.length === 0) && (
              <tr><td colSpan={6} className="text-center text-muted" style={{ padding: 40 }}>Belum ada data absensi.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================
// TAB: PENGGUNAAN SPAREPART (rekap per periode)
// Sumber: CM (sparepart dipakai pada pekerjaan) + Gudang (dokumen Stok Keluar)
// =============================================
const fmtRp = (n) => `Rp ${(Number(n) || 0).toLocaleString('id-ID', { maximumFractionDigits: 0 })}`;
const fmtQty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? v.toLocaleString('id-ID') : v.toLocaleString('id-ID', { maximumFractionDigits: 2 });
};

const USAGE_SUB_TABS = [
  { key: 'sparepart', label: 'Per Sparepart', icon: Package },
  { key: 'klien', label: 'Per Klien', icon: Layers },
  { key: 'teknisi', label: 'Per Teknisi', icon: User },
  { key: 'kendaraan', label: 'Per Kendaraan', icon: Truck },
  { key: 'kontrak', label: 'Per Kontrak', icon: FileSignature },
  { key: 'analisa', label: 'Analisa CM', icon: BarChart3 },
];

// =============================================
// ANALISA CM — perbandingan CM pakai sparepart vs tanpa sparepart
// (dipakai sebagai sub-tab di dalam tab Penggunaan Sparepart)
// =============================================
function TabAnalisaCm({ tanggalStart, tanggalEnd, noKontrak }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('semua');
  const [search, setSearch] = useState('');
  const [dimensi, setDimensi] = useState('klien');

  useEffect(() => {
    let dibatalkan = false;
    setLoading(true);
    setError('');
    setData(null);
    getLaporanAnalisaCm({ tanggal_start: tanggalStart, tanggal_end: tanggalEnd, no_kontrak: noKontrak || '' })
      .then(res => { if (!dibatalkan) setData(res.data); })
      .catch(err => {
        console.error('Failed to load CM analysis', err);
        if (!dibatalkan) setError('Gagal memuat analisa CM. Pastikan server backend berjalan.');
      })
      .finally(() => { if (!dibatalkan) setLoading(false); });
    return () => { dibatalkan = true; };
  }, [tanggalStart, tanggalEnd, noKontrak]);

  if (loading) return <div className="page-loading"><div className="spinner-lg" /><p>Memuat analisa CM...</p></div>;
  if (error) return <div className="dash-error"><AlertTriangle size={32} /><p>{error}</p></div>;
  if (!data) return null;

  const r = data.ringkasan || {};
  const biaya = data.biaya || [];
  const tren = data.tren_bulanan || [];
  const list = data.cm_list || [];
  const ko = data.komposisi || {};
  const periodLabel = `${tanggalStart} s.d ${tanggalEnd}`;
  const kontrakLabel = noKontrak ? `No Kontrak: ${noKontrak}` : 'Semua No Kontrak';

  const pakai = biaya.find(b => b.kelompok === 'Pakai Sparepart') || {};
  const tanpa = biaya.find(b => b.kelompok === 'Tanpa Sparepart') || {};
  const selisihRata = (pakai.rata_nominal || 0) - (tanpa.rata_nominal || 0);
  const rasioRata = (tanpa.rata_nominal || 0) > 0 ? (pakai.rata_nominal || 0) / (tanpa.rata_nominal || 0) : 0;

  const q = search.trim().toLowerCase();
  const listTampil = list
    .filter(c => filter === 'semua' ? true : (filter === 'pakai' ? c.pakai_sparepart : !c.pakai_sparepart))
    .filter(c => !q || [c.no_ref, c.klien, c.kendaraan, c.teknisi, c.jenis_pekerjaan]
      .some(v => (v || '').toLowerCase().includes(q)));

  const pieData = [
    { name: 'Pakai Sparepart', value: r.pakai_sparepart || 0, color: '#2dce89' },
    { name: 'Tanpa Sparepart', value: r.tanpa_sparepart || 0, color: '#fb6340' },
  ];
  const komposisiData = [
    { name: 'SP + Jasa', jumlah: ko.sp_dan_jasa || 0 },
    { name: 'SP saja', jumlah: ko.sp_saja || 0 },
    { name: 'Jasa saja', jumlah: ko.jasa_saja || 0 },
    { name: 'Tanpa keduanya', jumlah: ko.tanpa_keduanya || 0 },
  ];
  const dimensiRows = dimensi === 'klien' ? (data.per_klien || [])
    : dimensi === 'teknisi' ? (data.per_teknisi || []) : (data.per_status || []);

  const exportCSV = () => {
    const rows = [
      ['LAPORAN ANALISA CM — PAKAI SPAREPART VS TANPA SPAREPART'],
      [`Periode: ${periodLabel}`],
      [`Filter: ${kontrakLabel}`],
      [],
      ['Total CM', r.total_cm || 0],
      ['Pakai Sparepart', r.pakai_sparepart || 0, `${r.persen_pakai || 0}%`],
      ['Tanpa Sparepart', r.tanpa_sparepart || 0, `${r.persen_tanpa || 0}%`],
      ['Item Sparepart', r.total_item_sp || 0],
      ['Total Qty Sparepart', r.total_qty_sp || 0],
      ['Total Nilai Sparepart (Rp)', r.total_nilai_sp || 0],
      [],
      ['KOMPOSISI PEKERJAAN'],
      ['Pakai Sparepart + Jasa', ko.sp_dan_jasa || 0],
      ['Pakai Sparepart saja', ko.sp_saja || 0],
      ['Jasa saja (tanpa sparepart)', ko.jasa_saja || 0],
      ['Tanpa sparepart & jasa', ko.tanpa_keduanya || 0],
      [],
      ['PERBANDINGAN BIAYA'],
      ['Kelompok', 'Jumlah CM', 'Total Nominal (Rp)', 'Rata-rata (Rp)'],
      ...biaya.map(b => [b.kelompok, b.jumlah_cm, b.total_nominal, Math.round(b.rata_nominal)]),
      [],
      ['PER KLIEN'],
      ['Klien', 'Total CM', 'Pakai SP', 'Tanpa SP', '% Pakai', 'Qty SP', 'Nilai SP (Rp)', 'Rata Nominal (Rp)'],
      ...(data.per_klien || []).map(x => [x.label, x.total, x.pakai_sparepart, x.tanpa_sparepart, x.persen_pakai, x.qty_sp, x.nilai_sp, Math.round(x.rata_nominal)]),
      [],
      ['PER TEKNISI'],
      ['Teknisi', 'Total CM', 'Pakai SP', 'Tanpa SP', '% Pakai', 'Qty SP', 'Nilai SP (Rp)', 'Rata Nominal (Rp)'],
      ...(data.per_teknisi || []).map(x => [x.label, x.total, x.pakai_sparepart, x.tanpa_sparepart, x.persen_pakai, x.qty_sp, x.nilai_sp, Math.round(x.rata_nominal)]),
      [],
      ['PER STATUS'],
      ['Status', 'Total CM', 'Pakai SP', 'Tanpa SP', '% Pakai'],
      ...(data.per_status || []).map(x => [x.label, x.total, x.pakai_sparepart, x.tanpa_sparepart, x.persen_pakai]),
      [],
      ['TREN 12 BULAN'],
      ['Bulan', 'Total CM', 'Pakai Sparepart', 'Tanpa Sparepart'],
      ...tren.map(t => [t.label, t.total, t.pakai_sparepart, t.tanpa_sparepart]),
      [],
      ['DAFTAR CM'],
      ['No. Ref', 'Tanggal', 'No Kontrak', 'Klien', 'Kendaraan', 'Teknisi', 'Status', 'Pakai SP', 'Pakai Jasa', 'Item SP', 'Qty SP', 'Nilai SP (Rp)', 'Nominal Tagihan (Rp)'],
      ...listTampil.map(c => [c.no_ref, c.tanggal, c.no_kontrak || '-', c.klien, c.kendaraan, c.teknisi, c.status,
        c.pakai_sparepart ? 'Ya' : 'Tidak', c.pakai_jasa ? 'Ya' : 'Tidak',
        c.jumlah_item_sp, c.qty_sp, c.nilai_sp, c.nominal_tagihan]),
    ];
    const csv = rows.map(x => x.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Analisa_CM_${tanggalStart}_sd_${tanggalEnd}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const generatePDF = () => {
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;

    pdfHeader(doc, 'LAPORAN ANALISA CM', `Perbandingan CM pakai sparepart vs tanpa sparepart · ${periodLabel} · ${kontrakLabel}`, pageW);

    const startY = 46;
    doc.setDrawColor(230, 230, 230);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 28, 3, 3, 'FD');
    const statW = (pageW - 2 * margin - 15) / 3;
    pdfStatBox(doc, margin + 5, startY, statW, 'Total CM', r.total_cm || 0, [94, 114, 228]);
    pdfStatBox(doc, margin + statW + 10, startY, statW, `Pakai Sparepart (${r.persen_pakai || 0}%)`, r.pakai_sparepart || 0, [45, 206, 137]);
    pdfStatBox(doc, margin + 2 * (statW + 5) + 5, startY, statW, `Tanpa Sparepart (${r.persen_tanpa || 0}%)`, r.tanpa_sparepart || 0, [251, 99, 64]);

    let cursor = startY + 42;
    doc.setTextColor(23, 43, 77);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('PERBANDINGAN BIAYA & KOMPOSISI', margin, cursor);
    autoTable(doc, {
      ...pdfAutoTableOpts(cursor + 4, margin),
      head: [['Kelompok', 'Jumlah CM', 'Total Nominal', 'Rata-rata per CM']],
      body: [
        ...biaya.map(b => [b.kelompok, fmtQty(b.jumlah_cm), fmtRp(b.total_nominal), fmtRp(b.rata_nominal)]),
        ['Selisih rata-rata (Pakai − Tanpa)', '', '', fmtRp(selisihRata)],
        [],
        ['KOMPOSISI PEKERJAAN', 'Jumlah CM', '', ''],
        ['Pakai Sparepart + Jasa', fmtQty(ko.sp_dan_jasa), '', ''],
        ['Pakai Sparepart saja', fmtQty(ko.sp_saja), '', ''],
        ['Jasa saja (tanpa sparepart)', fmtQty(ko.jasa_saja), '', ''],
        ['Tanpa sparepart & jasa', fmtQty(ko.tanpa_keduanya), '', ''],
      ],
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
    });

    const headDimensi = ['No', 'Nama', 'Total CM', 'Pakai SP', 'Tanpa SP', '% Pakai', 'Qty SP', 'Nilai SP', 'Rata Nominal'];
    const bodyDimensi = (rows) => rows.map((x, i) => [
      String(i + 1), x.label, fmtQty(x.total), fmtQty(x.pakai_sparepart), fmtQty(x.tanpa_sparepart),
      `${x.persen_pakai || 0}%`, fmtQty(x.qty_sp), fmtRp(x.nilai_sp), fmtRp(x.rata_nominal),
    ]);
    const colDimensi = {
      0: { halign: 'center', cellWidth: 10 }, 2: { halign: 'right' }, 3: { halign: 'right' },
      4: { halign: 'right' }, 5: { halign: 'right', cellWidth: 16 }, 6: { halign: 'right' },
      7: { halign: 'right' }, 8: { halign: 'right' },
    };

    let y = doc.lastAutoTable.finalY + 14;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('PER KLIEN', margin, y);
    autoTable(doc, { ...pdfAutoTableOpts(y + 4, margin), head: [headDimensi], body: bodyDimensi(data.per_klien || []), columnStyles: colDimensi });

    y = doc.lastAutoTable.finalY + 14;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('PER TEKNISI', margin, y);
    autoTable(doc, { ...pdfAutoTableOpts(y + 4, margin), head: [headDimensi], body: bodyDimensi(data.per_teknisi || []), columnStyles: colDimensi });

    y = doc.lastAutoTable.finalY + 14;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('TREN 12 BULAN & PER STATUS', margin, y);
    autoTable(doc, {
      ...pdfAutoTableOpts(y + 4, margin),
      head: [['Bulan / Status', 'Total CM', 'Pakai Sparepart', 'Tanpa Sparepart', '% Pakai']],
      body: [
        ...tren.map(t => [t.label, fmtQty(t.total), fmtQty(t.pakai_sparepart), fmtQty(t.tanpa_sparepart), '']),
        [],
        ...(data.per_status || []).map(x => [x.label, fmtQty(x.total), fmtQty(x.pakai_sparepart), fmtQty(x.tanpa_sparepart), `${x.persen_pakai || 0}%`]),
      ],
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right', cellWidth: 18 } },
    });

    doc.setPage(doc.internal.getNumberOfPages());
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(136, 152, 170);
    doc.text('CM dianggap "pakai sparepart" bila memiliki minimal 1 item sparepart pada data CM. Angka mencakup semua status CM pada periode.', margin, doc.internal.pageSize.getHeight() - 26);

    pdfFooter(doc, pageW);
    doc.save(`Analisa_CM_${tanggalStart}_sd_${tanggalEnd}.pdf`);
  };

  return (
    <div>
      <div className="page-header-row" style={{ marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-600)', margin: 0 }}>
            Analisa CM — Pakai Sparepart vs Tanpa Sparepart
          </h3>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Periode: {periodLabel} · {kontrakLabel}</span>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={exportCSV} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileSpreadsheet size={16} /> Export CSV
          </button>
          <button className="btn btn-primary" onClick={generatePDF} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={16} /> Export PDF
          </button>
        </div>
      </div>

      {/* Kartu ringkasan */}
      <div className="dash-stats-row" style={{ marginBottom: 20 }}>
        <div className="dash-stat-col">
          <StatCardMini icon={Wrench} label="Total CM" value={r.total_cm || 0} color="#5e72e4"
            sublabel="Semua status CM pada periode" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={Package} label="Pakai Sparepart" value={r.pakai_sparepart || 0} color="#2dce89"
            sublabel={`${r.persen_pakai || 0}% dari ${r.total_cm || 0} CM`} />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={AlertTriangle} label="Tanpa Sparepart" value={r.tanpa_sparepart || 0} color="#fb6340"
            sublabel={`${r.persen_tanpa || 0}% dari ${r.total_cm || 0} CM`} />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={PackageSearch} label="Item Sparepart" value={r.total_item_sp || 0} color="#11cdef"
            sublabel={`Qty ${fmtQty(r.total_qty_sp)} · ${fmtRp(r.total_nilai_sp)}`} />
        </div>
      </div>

      {/* Rasio pakai vs tanpa (jawaban langsung pertanyaan user) */}
      <div className="dash-charts-row" style={{ marginBottom: 20 }}>
        <div className="dash-chart-col-4">
          <ChartCard title="Perbandingan CM" subtitle={`${r.total_cm || 0} CM pada periode ini`}>
            {pieData.some(p => p.value > 0) ? (
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                      {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-legend">
                  {pieData.map((d, i) => (
                    <div key={i} className="donut-legend-item">
                      <span className="donut-dot" style={{ background: d.color }} />
                      <span>{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-muted text-sm">Belum ada data CM pada periode ini.</p>}
          </ChartCard>
        </div>

        <div className="dash-chart-col-4">
          <ChartCard title="Komposisi Pekerjaan CM" subtitle="Sparepart vs jasa">
            {komposisiData.some(k => k.jumlah > 0) ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={komposisiData} margin={{ top: 10, right: 10, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                  <XAxis dataKey="name" stroke="#8898aa" fontSize={10} />
                  <YAxis stroke="#8898aa" fontSize={11} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="jumlah" name="Jumlah CM" radius={[6, 6, 0, 0]} barSize={38}>
                    {komposisiData.map((_, i) => <Cell key={i} fill={['#2dce89', '#11cdef', '#fb6340', '#8898aa'][i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>

        <div className="dash-chart-col-4">
          <ChartCard title="Perbandingan Biaya" subtitle={`Rata-rata per CM · ${periodLabel}`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0' }}>
              {biaya.map((b, i) => (
                <div key={i}>
                  <div className="stat-progress-header">
                    <span className="stat-progress-label">{b.kelompok} ({b.jumlah_cm} CM)</span>
                    <span className="stat-progress-value">{fmtRp(b.rata_nominal)}</span>
                  </div>
                  <div className="stat-progress-bar">
                    <div className="stat-progress-fill" style={{
                      width: `${Math.min(((b.rata_nominal || 0) / Math.max(pakai.rata_nominal || 0, tanpa.rata_nominal || 0, 1)) * 100, 100)}%`,
                      background: i === 0 ? '#2dce89' : '#fb6340',
                    }} />
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 4 }}>Total {fmtRp(b.total_nominal)}</div>
                </div>
              ))}
              <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 10, fontSize: '0.78rem', color: 'var(--gray-600)' }}>
                Selisih rata-rata: <strong>{fmtRp(selisihRata)}</strong>
                {rasioRata > 0 && <span> ({rasioRata.toFixed(1)}× lebih besar)</span>}
              </div>
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Tren 12 bulan */}
      <div className="dash-charts-row" style={{ marginBottom: 20 }}>
        <div className="dash-chart-col-12">
          <ChartCard title="Tren 12 Bulan — CM Pakai vs Tanpa Sparepart"
            subtitle={tren.length > 0 ? `${tren[0].label} – ${tren[tren.length - 1].label}` : '12 bulan terakhir'}>
            {tren.length > 0 ? (
              <>
                <div style={{ display: 'flex', gap: 18, marginBottom: 8, fontSize: '0.75rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: '#2dce89' }} /> Pakai Sparepart
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: '#fb6340' }} /> Tanpa Sparepart
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={tren} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                    <XAxis dataKey="label" stroke="#8898aa" fontSize={10} angle={-25} textAnchor="end" height={58} />
                    <YAxis stroke="#8898aa" fontSize={11} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="pakai_sparepart" name="Pakai Sparepart" fill="#2dce89" radius={[5, 5, 0, 0]} barSize={20} />
                    <Bar dataKey="tanpa_sparepart" name="Tanpa Sparepart" fill="#fb6340" radius={[5, 5, 0, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>
      </div>

      {/* Tabel dimensi */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 600 }}>Rincian analisa per:</span>
        {[{ key: 'klien', label: 'Klien' }, { key: 'teknisi', label: 'Teknisi' }, { key: 'status', label: 'Status' }].map(t => (
          <button key={t.key} onClick={() => setDimensi(t.key)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', borderRadius: 8,
            fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
            background: dimensi === t.key ? 'var(--primary)' : 'white',
            color: dimensi === t.key ? 'white' : 'var(--gray-600)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <Layers size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 50 }}>No</th>
              <th>{dimensi === 'klien' ? 'Klien' : dimensi === 'teknisi' ? 'Teknisi' : 'Status CM'}</th>
              <th style={{ textAlign: 'center' }}>Total CM</th>
              <th style={{ textAlign: 'center' }}>Pakai SP</th>
              <th style={{ textAlign: 'center' }}>Tanpa SP</th>
              <th style={{ textAlign: 'center' }}>% Pakai</th>
              <th style={{ textAlign: 'right' }}>Qty SP</th>
              <th style={{ textAlign: 'right' }}>Nilai SP</th>
              <th style={{ textAlign: 'right' }}>Rata Nominal</th>
            </tr>
          </thead>
          <tbody>
            {dimensiRows.map((x, i) => (
              <tr key={`${x.label}-${i}`}>
                <td>{i + 1}</td>
                <td><strong>{x.label}</strong></td>
                <td style={{ textAlign: 'center', fontWeight: 600 }}>{x.total}</td>
                <td style={{ textAlign: 'center', color: '#2dce89', fontWeight: 600 }}>{x.pakai_sparepart}</td>
                <td style={{ textAlign: 'center', color: '#fb6340', fontWeight: 600 }}>{x.tanpa_sparepart}</td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{
                    background: (x.persen_pakai || 0) >= 50 ? '#d1fae5' : '#fee2e2',
                    color: (x.persen_pakai || 0) >= 50 ? '#065f46' : '#991b1b',
                    padding: '2px 8px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 700,
                  }}>{x.persen_pakai || 0}%</span>
                </td>
                <td style={{ textAlign: 'right' }}>{fmtQty(x.qty_sp)}</td>
                <td style={{ textAlign: 'right' }}>{fmtRp(x.nilai_sp)}</td>
                <td style={{ textAlign: 'right' }}>{fmtRp(x.rata_nominal)}</td>
              </tr>
            ))}
            {dimensiRows.length === 0 && (
              <tr><td colSpan={9} className="text-center text-muted" style={{ padding: 40 }}>Tidak ada data.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Daftar CM */}
      <div className="page-header-row" style={{ marginTop: 24, marginBottom: 16 }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          Daftar CM ({listTampil.length} dari {list.length})
        </h3>
        <div className="page-header-actions">
          <div className="search-box">
            <Search size={16} />
            <input placeholder="Cari CM / klien / teknisi..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[{ key: 'semua', label: 'Semua' }, { key: 'pakai', label: 'Pakai Sparepart' }, { key: 'tanpa', label: 'Tanpa Sparepart' }].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '7px 14px', border: '1px solid var(--gray-200)', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
            background: filter === f.key ? 'var(--primary)' : 'white',
            color: filter === f.key ? 'white' : 'var(--gray-600)',
          }}>{f.label}</button>
        ))}
      </div>

      {data.list_terpotong && (
        <div style={{ background: '#fef3c7', color: '#92400e', padding: '10px 14px', borderRadius: 8, fontSize: '0.78rem', marginBottom: 12 }}>
          Daftar CM dibatasi {list.length} baris terbaru. Angka rekap tetap dihitung dari seluruh CM pada periode.
        </div>
      )}

      <div className="table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 50 }}>No</th>
              <th>No. Ref</th>
              <th>Tanggal</th>
              <th style={{ textAlign: 'center' }}>No Kontrak</th>
              <th>Klien</th>
              <th>Kendaraan</th>
              <th>Teknisi</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th style={{ textAlign: 'center' }}>Sparepart</th>
              <th style={{ textAlign: 'center' }}>Jasa</th>
              <th style={{ textAlign: 'center' }}>Item SP</th>
              <th style={{ textAlign: 'right' }}>Nilai SP</th>
              <th style={{ textAlign: 'right' }}>Nominal</th>
            </tr>
          </thead>
          <tbody>
            {listTampil.slice(0, 200).map((c, i) => (
              <tr key={c.id}>
                <td>{i + 1}</td>
                <td><code>{c.no_ref}</code></td>
                <td style={{ whiteSpace: 'nowrap' }}>{c.tanggal}</td>
                <td style={{ textAlign: 'center' }}>{c.no_kontrak || '-'}</td>
                <td>{c.klien || '-'}</td>
                <td>{c.kendaraan || '-'}</td>
                <td>{c.teknisi || '-'}</td>
                <td style={{ textAlign: 'center' }}><StatusBadge status={c.status} /></td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{
                    background: c.pakai_sparepart ? '#d1fae5' : '#fee2e2',
                    color: c.pakai_sparepart ? '#065f46' : '#991b1b',
                    padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700,
                  }}>{c.pakai_sparepart ? 'Ya' : 'Tidak'}</span>
                </td>
                <td style={{ textAlign: 'center' }}>{c.pakai_jasa ? 'Ya' : 'Tidak'}</td>
                <td style={{ textAlign: 'center' }}>{c.jumlah_item_sp || 0}</td>
                <td style={{ textAlign: 'right' }}>{fmtRp(c.nilai_sp)}</td>
                <td style={{ textAlign: 'right' }}>{fmtRp(c.nominal_tagihan)}</td>
              </tr>
            ))}
            {listTampil.length === 0 && (
              <tr><td colSpan={13} className="text-center text-muted" style={{ padding: 40 }}>Tidak ada CM yang cocok.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {listTampil.length > 200 && (
        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', textAlign: 'center', padding: 10 }}>
          Menampilkan 200 dari {listTampil.length} baris — gunakan Export CSV untuk daftar lengkap.
        </div>
      )}
    </div>
  );
}

function UsageGroupTable({ rows, firstCol }) {
  if (!rows || rows.length === 0) {
    return <p className="text-muted text-sm" style={{ padding: 20 }}>Tidak ada data pada periode ini.</p>;
  }
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th style={{ width: 50 }}>No</th>
            <th>{firstCol}</th>
            <th style={{ textAlign: 'right' }}>Qty</th>
            <th style={{ textAlign: 'right' }}>Nilai</th>
            <th style={{ textAlign: 'center' }}>Transaksi</th>
            <th style={{ textAlign: 'center' }}>Asal</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((g, i) => (
            <tr key={`${g.label}-${i}`}>
              <td>{i + 1}</td>
              <td><strong>{g.label}</strong></td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtQty(g.qty)}</td>
              <td style={{ textAlign: 'right' }}>{fmtRp(g.nilai)}</td>
              <td style={{ textAlign: 'center' }}>{g.transaksi || 0}</td>
              <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {g.qty_cm > 0 && (
                  <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600, marginRight: 4 }}>
                    CM {fmtQty(g.qty_cm)}
                  </span>
                )}
                {g.qty_keluar > 0 && (
                  <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600 }}>
                    Gudang {fmtQty(g.qty_keluar)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TabPenggunaanSparepart() {
  const pad2 = (n) => String(n).padStart(2, '0');
  const todayISO = () => new Date().toLocaleDateString('en-CA');
  const monthStartISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`;
  };

  const [dateStart, setDateStart] = useState(monthStartISO());
  const [dateEnd, setDateEnd] = useState(todayISO());
  const [noKontrak, setNoKontrak] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [subTab, setSubTab] = useState('sparepart');
  const [openPart, setOpenPart] = useState('');

  const load = (start, end, kontrak) => {
    setLoading(true);
    setError('');
    setOpenPart('');
    getLaporanPenggunaanSparepart({ tanggal_start: start, tanggal_end: end, no_kontrak: kontrak || '' })
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Failed to load sparepart usage report', err);
        setError('Gagal memuat laporan penggunaan sparepart. Pastikan server backend berjalan.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(monthStartISO(), todayISO(), '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyBulanIni = () => {
    const s = monthStartISO();
    const e = todayISO();
    setDateStart(s);
    setDateEnd(e);
    load(s, e, noKontrak);
  };

  // Ganti No Kontrak langsung memuat ulang supaya admin tidak perlu klik Terapkan 2x.
  const gantiKontrak = (nilai) => {
    setNoKontrak(nilai);
    load(dateStart, dateEnd, nilai);
  };

  if (loading) {
    return <div className="page-loading"><div className="spinner-lg" /><p>Memuat laporan penggunaan sparepart...</p></div>;
  }
  if (error) {
    return <div className="dash-error"><AlertTriangle size={32} /><p>{error}</p></div>;
  }
  if (!data) return null;

  const s = data.summary || {};
  const perSparepart = data.per_sparepart || [];
  const detail = data.detail || [];
  const tren = data.tren_bulanan || [];
  const periodLabel = `${dateStart} s.d ${dateEnd}`;

  const q = search.trim().toLowerCase();
  const filteredParts = q
    ? perSparepart.filter(p => (p.nama || '').toLowerCase().includes(q) || (p.kode || '').toLowerCase().includes(q))
    : perSparepart;

  const detailFor = (nama) => detail.filter(d => (d.nama || '') === nama);
  const topParts = perSparepart.slice(0, 10).map(p => ({ nama: p.nama, qty: p.qty }));
  const klienPie = (data.per_klien || []).slice(0, 6).map((g, i) => ({
    name: g.label, value: g.nilai, color: COLORS[i % COLORS.length],
  }));

  const inputStyle = { padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' };
  const kontrakOptions = data.kontrak_options || [];
  const kontrakLabel = noKontrak ? `No Kontrak: ${noKontrak}` : 'Semua No Kontrak';

  const exportCSV = () => {
    const rows = [
      ['LAPORAN PENGGUNAAN SPAREPART'],
      [`Periode: ${periodLabel}`],
      [`Filter: ${kontrakLabel}`],
      [],
      ['Jenis Sparepart', s.total_sparepart || 0],
      ['Total Qty', s.total_qty || 0],
      ['Total Nilai (Rp)', s.total_nilai || 0],
      ['Total Transaksi', s.total_transaksi || 0],
      [],
      ['No', 'Kode', 'Nama Sparepart', 'Kategori', 'Qty', 'Satuan', 'Nilai (Rp)', 'Qty CM', 'Qty Gudang', 'Transaksi'],
      ...perSparepart.map((p, i) => [
        i + 1, p.kode || '', p.nama || '', p.kategori || '', p.qty || 0, p.satuan || '',
        p.nilai || 0, p.qty_cm || 0, p.qty_keluar || 0, p.transaksi || 0,
      ]),
      [],
      ['RINCIAN PER KLIEN'],
      ['Klien', 'Qty', 'Nilai (Rp)', 'Transaksi'],
      ...(data.per_klien || []).map(g => [g.label, g.qty, g.nilai, g.transaksi]),
      [],
      ['RINCIAN PER TEKNISI / PENERIMA'],
      ['Teknisi / Penerima', 'Qty', 'Nilai (Rp)', 'Transaksi'],
      ...(data.per_teknisi || []).map(g => [g.label, g.qty, g.nilai, g.transaksi]),
      [],
      ['RINCIAN PER KENDARAAN'],
      ['Kendaraan', 'Qty', 'Nilai (Rp)', 'Transaksi'],
      ...(data.per_kendaraan || []).map(g => [g.label, g.qty, g.nilai, g.transaksi]),
      [],
      ['RINCIAN PER KONTRAK'],
      ['No Kontrak', 'Qty', 'Nilai (Rp)', 'Transaksi'],
      ...(data.per_kontrak || []).map(g => [g.label, g.qty, g.nilai, g.transaksi]),
      [],
      ['TREN 12 BULAN'],
      ['Bulan', 'Qty CM', 'Qty Gudang', 'Total Qty', 'Nilai (Rp)', 'Transaksi'],
      ...tren.map(t => [t.label, t.qty_cm || 0, t.qty_keluar || 0, t.qty || 0, t.nilai || 0, t.transaksi || 0]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Laporan_Penggunaan_Sparepart_${dateStart}_sd_${dateEnd}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const generatePDF = () => {
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;

    pdfHeader(doc, 'LAPORAN PENGGUNAAN SPAREPART', `Periode: ${periodLabel} · ${kontrakLabel}`, pageW);

    const startY = 46;
    doc.setDrawColor(230, 230, 230);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 28, 3, 3, 'FD');
    const statW = (pageW - 2 * margin - 15) / 3;
    pdfStatBox(doc, margin + 5, startY, statW, 'Jenis Sparepart', s.total_sparepart || 0, [17, 205, 239]);
    pdfStatBox(doc, margin + statW + 10, startY, statW, 'Total Qty', fmtQty(s.total_qty), [45, 206, 137]);
    pdfStatBox(doc, margin + 2 * (statW + 5) + 5, startY, statW, 'Total Nilai', fmtRp(s.total_nilai), [94, 114, 228]);

    const cursor = startY + 42;
    doc.setTextColor(23, 43, 77);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`RINCIAN PER SPAREPART (${perSparepart.length} jenis)`, margin, cursor);

    autoTable(doc, {
      ...pdfAutoTableOpts(cursor + 4, margin),
      head: [['No', 'Kode', 'Nama Sparepart', 'Kategori', 'Qty', 'Satuan', 'Nilai', 'Trx']],
      body: perSparepart.map((p, i) => [
        String(i + 1), p.kode || '-', p.nama || '-', p.kategori || '-',
        fmtQty(p.qty), p.satuan || '-', fmtRp(p.nilai), String(p.transaksi || 0),
      ]),
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { cellWidth: 24 },
        4: { halign: 'right' },
        5: { halign: 'center', cellWidth: 18 },
        6: { halign: 'right' },
        7: { halign: 'center', cellWidth: 14 },
      },
    });

    let y = doc.lastAutoTable.finalY + 14;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('RINCIAN PER KLIEN', margin, y);
    autoTable(doc, {
      ...pdfAutoTableOpts(y + 4, margin),
      head: [['No', 'Klien', 'Qty', 'Nilai', 'Trx']],
      body: (data.per_klien || []).map((g, i) => [String(i + 1), g.label, fmtQty(g.qty), fmtRp(g.nilai), String(g.transaksi || 0)]),
      columnStyles: { 0: { halign: 'center', cellWidth: 10 }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'center', cellWidth: 14 } },
    });

    y = doc.lastAutoTable.finalY + 14;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('RINCIAN PER TEKNISI / PENERIMA', margin, y);
    autoTable(doc, {
      ...pdfAutoTableOpts(y + 4, margin),
      head: [['No', 'Teknisi / Penerima', 'Qty', 'Nilai', 'Trx']],
      body: (data.per_teknisi || []).map((g, i) => [String(i + 1), g.label, fmtQty(g.qty), fmtRp(g.nilai), String(g.transaksi || 0)]),
      columnStyles: { 0: { halign: 'center', cellWidth: 10 }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'center', cellWidth: 14 } },
    });

    y = doc.lastAutoTable.finalY + 14;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('RINCIAN PER KONTRAK', margin, y);
    autoTable(doc, {
      ...pdfAutoTableOpts(y + 4, margin),
      head: [['No', 'No Kontrak', 'Qty', 'Nilai', 'Trx']],
      body: (data.per_kontrak || []).map((g, i) => [String(i + 1), g.label, fmtQty(g.qty), fmtRp(g.nilai), String(g.transaksi || 0)]),
      columnStyles: { 0: { halign: 'center', cellWidth: 10 }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'center', cellWidth: 14 } },
    });

    y = doc.lastAutoTable.finalY + 14;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('TREN PENGGUNAAN 12 BULAN', margin, y);
    autoTable(doc, {
      ...pdfAutoTableOpts(y + 4, margin),
      head: [['No', 'Bulan', 'Qty CM', 'Qty Gudang', 'Total Qty', 'Nilai', 'Trx']],
      body: tren.map((t, i) => [
        String(i + 1), t.label, fmtQty(t.qty_cm), fmtQty(t.qty_keluar), fmtQty(t.qty), fmtRp(t.nilai), String(t.transaksi || 0),
      ]),
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'center', cellWidth: 14 },
      },
    });

    doc.setPage(doc.internal.getNumberOfPages());
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(136, 152, 170);
    doc.text('Sumber: CM (sparepart dipakai pada pekerjaan) + Gudang (dokumen Stok Keluar). Nilai memakai harga jual.', margin, doc.internal.pageSize.getHeight() - 26);

    pdfFooter(doc, pageW);
    doc.save(`Laporan_Penggunaan_Sparepart_${dateStart}_sd_${dateEnd}.pdf`);
  };

  return (
    <div>
      {/* Filter periode + export */}
      <div className="filters" style={{ marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
          <label style={{ fontSize: '0.75rem', marginBottom: 4, color: 'var(--gray-400)' }}>Tanggal Mulai</label>
          <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} style={inputStyle} />
        </div>
        <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
          <label style={{ fontSize: '0.75rem', marginBottom: 4, color: 'var(--gray-400)' }}>Tanggal Akhir</label>
          <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} style={inputStyle} />
        </div>
        <div className="form-group" style={{ margin: 0, minWidth: 190 }}>
          <label style={{ fontSize: '0.75rem', marginBottom: 4, color: 'var(--gray-400)' }}>No Kontrak (Armada)</label>
          <select value={noKontrak} onChange={e => gantiKontrak(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
            <option value="">Semua No Kontrak</option>
            {kontrakOptions.map(k => (
              <option key={k.no_kontrak} value={k.no_kontrak}>
                {k.no_kontrak} — {k.jumlah_armada} armada
              </option>
            ))}
          </select>
        </div>
        <button className="btn btn-primary" onClick={() => load(dateStart, dateEnd, noKontrak)} style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Filter size={16} /> Terapkan
        </button>
        <button className="btn btn-secondary" onClick={applyBulanIni} style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 6 }}>
          <CalendarDays size={16} /> Bulan Ini
        </button>
        {subTab !== 'analisa' && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, marginTop: 22 }}>
            <button className="btn btn-secondary" onClick={exportCSV} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileSpreadsheet size={16} /> Export CSV
            </button>
            <button className="btn btn-primary" onClick={generatePDF} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileText size={16} /> Export PDF
            </button>
          </div>
        )}
      </div>

      {noKontrak !== '' && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8, background: '#e0f2fe', color: '#075985',
          padding: '10px 14px', borderRadius: 8, fontSize: '0.78rem', marginBottom: 16,
        }}>
          <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Filter <strong>No Kontrak: {noKontrak}</strong> aktif — angka hanya dihitung dari CM armada
            yang terdaftar pada kontrak tersebut. Data <strong>Gudang (Stok Keluar)</strong> tidak dihitung
            karena dokumen gudang tidak terhubung ke armada.
          </span>
        </div>
      )}

      {/* Bagian rekap sparepart — disembunyikan saat sub-tab Analisa CM aktif */}
      {subTab !== 'analisa' && (<>
      {/* Kartu ringkasan */}
      <div className="dash-stats-row" style={{ marginBottom: 20 }}>
        <div className="dash-stat-col">
          <StatCardMini icon={PackageSearch} label="Jenis Sparepart" value={s.total_sparepart || 0} color="#11cdef"
            sublabel="Sparepart berbeda terpakai" />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={Package} label="Total Qty" value={s.total_qty || 0} color="#2dce89"
            sublabel={`CM: ${fmtQty(s.qty_cm)} · Gudang: ${fmtQty(s.qty_keluar)}`} />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={DollarSign} label="Total Nilai" value={fmtRp(s.total_nilai)} color="#5e72e4"
            sublabel={`CM: ${fmtRp(s.nilai_cm)} · Gudang: ${fmtRp(s.nilai_keluar)}`} />
        </div>
        <div className="dash-stat-col">
          <StatCardMini icon={TrendingUp} label="Transaksi" value={s.total_transaksi || 0} color="#fb6340"
            sublabel={`CM: ${s.trx_cm || 0} · Stok Keluar: ${s.trx_keluar || 0}`} />
        </div>
      </div>

      {/* Grafik */}
      <div className="dash-charts-row" style={{ marginBottom: 20 }}>
        <div className="dash-chart-col-6">
          <ChartCard title="Top 10 Sparepart" subtitle={`Paling banyak dipakai · ${periodLabel}`}>
            {topParts.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topParts} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" horizontal={false} />
                  <XAxis type="number" stroke="#8898aa" fontSize={11} />
                  <YAxis type="category" dataKey="nama" stroke="#8898aa" fontSize={10} width={150} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="qty" name="Qty" radius={[0, 6, 6, 0]} barSize={16}>
                    {topParts.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-muted text-sm">Tidak ada pemakaian sparepart pada periode ini.</p>}
          </ChartCard>
        </div>

        <div className="dash-chart-col-6">
          <ChartCard title="Nilai per Klien" subtitle={`${periodLabel} · termasuk Gudang`}>
            {klienPie.length > 0 ? (
              <div className="donut-wrapper">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={klienPie} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                      {klienPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtRp(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="donut-legend">
                  {klienPie.map((d, i) => (
                    <div key={i} className="donut-legend-item">
                      <span className="donut-dot" style={{ background: d.color }} />
                      <span>{d.name}: {fmtRp(d.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>
      </div>

      {/* Tren 12 bulan */}
      <div className="dash-charts-row" style={{ marginBottom: 20 }}>
        <div className="dash-chart-col-12">
          <ChartCard
            title="Tren Penggunaan Sparepart (12 Bulan)"
            subtitle={tren.length > 0 ? `${tren[0].label} – ${tren[tren.length - 1].label}` : '12 bulan terakhir'}
          >
            {tren.length > 0 ? (
              <>
                <div style={{ display: 'flex', gap: 18, marginBottom: 8, fontSize: '0.75rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: '#11cdef' }} /> Qty CM
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: '#2dce89' }} /> Qty Gudang (Stok Keluar)
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={tren} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                    <XAxis dataKey="label" stroke="#8898aa" fontSize={10} angle={-25} textAnchor="end" height={58} />
                    <YAxis stroke="#8898aa" fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="qty_cm" name="Qty CM" fill="#11cdef" radius={[5, 5, 0, 0]} barSize={20} />
                    <Bar dataKey="qty_keluar" name="Qty Gudang" fill="#2dce89" radius={[5, 5, 0, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            ) : <p className="text-muted text-sm">Tidak ada data.</p>}
          </ChartCard>
        </div>
      </div>

      </>)}

      {/* Sub tab */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {USAGE_SUB_TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: 'none', borderRadius: 8,
              fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
              background: subTab === t.key ? 'var(--primary)' : 'white',
              color: subTab === t.key ? 'white' : 'var(--gray-600)',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {subTab === 'sparepart' && (
        <>
          <div className="page-header-row" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--gray-600)' }}>
              Sparepart Terpakai ({filteredParts.length} dari {perSparepart.length} jenis)
            </h3>
            <div className="page-header-actions">
              <div className="search-box">
                <Search size={16} />
                <input placeholder="Cari nama atau kode..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>

          {data.detail_terpotong && (
            <div style={{ background: '#fef3c7', color: '#92400e', padding: '10px 14px', borderRadius: 8, fontSize: '0.78rem', marginBottom: 12 }}>
              Rincian transaksi terlalu banyak — hanya {detail.length} baris terbaru yang bisa dibuka. Angka rekap tetap lengkap.
            </div>
          )}

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>No</th>
                  <th>Kode</th>
                  <th>Nama Sparepart</th>
                  <th>Kategori</th>
                  <th style={{ textAlign: 'right' }}>Qty</th>
                  <th>Satuan</th>
                  <th style={{ textAlign: 'right' }}>Nilai</th>
                  <th style={{ textAlign: 'center' }}>Transaksi</th>
                  <th style={{ textAlign: 'center' }}>Detail</th>
                </tr>
              </thead>
              <tbody>
                {filteredParts.map((p, i) => {
                  const isOpen = openPart === p.nama;
                  const rows = isOpen ? detailFor(p.nama) : [];
                  return (
                    <React.Fragment key={`${p.nama}-${i}`}>
                      <tr
                        onClick={() => setOpenPart(isOpen ? '' : p.nama)}
                        style={{ cursor: 'pointer', background: isOpen ? '#f8f9fc' : undefined }}
                      >
                        <td>{i + 1}</td>
                        <td><code>{p.kode || '-'}</code></td>
                        <td><strong>{p.nama}</strong></td>
                        <td>{p.kategori || '-'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtQty(p.qty)}</td>
                        <td>{p.satuan || '-'}</td>
                        <td style={{ textAlign: 'right' }}>{fmtRp(p.nilai)}</td>
                        <td style={{ textAlign: 'center' }}>{p.transaksi || 0}</td>
                        <td style={{ textAlign: 'center' }}>
                          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={9} style={{ background: '#f8f9fc', padding: 0 }}>
                            <div style={{ padding: '12px 16px' }}>
                              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8 }}>
                                DETAIL TRANSAKSI — {p.nama} ({rows.length} baris)
                              </div>
                              {rows.length === 0 ? (
                                <p className="text-muted text-sm" style={{ margin: 0 }}>
                                  Rincian transaksi tidak tersedia (di luar batas tampilan).
                                </p>
                              ) : (
                                <table className="data-table" style={{ fontSize: '0.78rem' }}>
                                  <thead>
                                    <tr>
                                      <th>Tanggal</th>
                                      <th>Sumber</th>
                                      <th>No. Ref</th>
                                      <th style={{ textAlign: 'right' }}>Qty</th>
                                      <th style={{ textAlign: 'right' }}>Harga</th>
                                      <th style={{ textAlign: 'right' }}>Nilai</th>
                                      <th>Klien</th>
                                      <th>Kendaraan</th>
                                      <th>Teknisi</th>
                                      <th>Keterangan</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {rows.map((d, j) => (
                                      <tr key={j}>
                                        <td style={{ whiteSpace: 'nowrap' }}>{d.tanggal}</td>
                                        <td>
                                          <span style={{
                                            background: d.sumber === 'CM' ? '#dbeafe' : '#d1fae5',
                                            color: d.sumber === 'CM' ? '#1e40af' : '#065f46',
                                            padding: '2px 8px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 600,
                                          }}>
                                            {d.sumber}
                                          </span>
                                        </td>
                                        <td><code>{d.no_ref}</code></td>
                                        <td style={{ textAlign: 'right' }}>{fmtQty(d.qty)}</td>
                                        <td style={{ textAlign: 'right' }}>{fmtRp(d.harga)}</td>
                                        <td style={{ textAlign: 'right' }}>{fmtRp(d.nilai)}</td>
                                        <td>{d.klien || '-'}</td>
                                        <td>{d.kendaraan || '-'}</td>
                                        <td>{d.teknisi || '-'}</td>
                                        <td style={{ maxWidth: 220 }}>{d.keterangan || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {filteredParts.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-muted" style={{ padding: 40 }}>
                      Tidak ada pemakaian sparepart pada periode ini.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {subTab === 'klien' && <UsageGroupTable rows={data.per_klien} firstCol="Klien" />}
      {subTab === 'teknisi' && <UsageGroupTable rows={data.per_teknisi} firstCol="Teknisi / Penerima" />}
      {subTab === 'kendaraan' && <UsageGroupTable rows={data.per_kendaraan} firstCol="Kendaraan" />}
      {subTab === 'kontrak' && <UsageGroupTable rows={data.per_kontrak} firstCol="No Kontrak" />}
      {subTab === 'analisa' && <TabAnalisaCm tanggalStart={dateStart} tanggalEnd={dateEnd} noKontrak={noKontrak} />}
    </div>
  );
}

// =============================================
// MAIN PAGE
// =============================================
export default function Laporan({ initialTab = 'kendaraan' }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFilters, setDateFilters] = useState({});

  // Mode khusus: hanya rekap absensi (route /laporan-absensi)
  const isAbsensiOnly = initialTab === 'absensi';
  const pageTitle = isAbsensiOnly ? 'Laporan Absensi' : 'Laporan';
  const pageSubtitle = isAbsensiOnly
    ? 'Rekap absensi karyawan'
    : 'Ringkasan data kendaraan, sparepart, maintenance, dan penggunaan sparepart';

  const fetchLaporan = (filters) => {
    setLoading(true);
    setError('');
    getLaporan(filters || dateFilters)
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Failed to load report data', err);
        setError('Gagal memuat data laporan. Pastikan server backend berjalan.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLaporan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateFilter = (filters) => {
    setDateFilters(filters);
    fetchLaporan(filters);
  };

  if (loading) {
    return (
      <div className="dash-wrapper">
        <div className="dash-header">
          <div className="dash-header-bg" />
          <div className="dash-header-content">
            <div className="dash-breadcrumb">
              <span>Laporan & Checklist</span>
              <span className="dash-breadcrumb-sep">/</span>
              <span className="dash-breadcrumb-current">{pageTitle}</span>
            </div>
            <h2 className="dash-title">{pageTitle}</h2>
            <p className="dash-subtitle">{pageSubtitle}</p>
          </div>
        </div>
        <div className="dash-content">
          <div className="dash-loading">
            <div className="spinner-lg" />
            <p>Memuat data laporan...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dash-wrapper">
        <div className="dash-header">
          <div className="dash-header-bg" />
          <div className="dash-header-content">
            <div className="dash-breadcrumb">
              <span>Laporan & Checklist</span>
              <span className="dash-breadcrumb-sep">/</span>
              <span className="dash-breadcrumb-current">{pageTitle}</span>
            </div>
            <h2 className="dash-title">{pageTitle}</h2>
            <p className="dash-subtitle">{pageSubtitle}</p>
          </div>
        </div>
        <div className="dash-content">
          <div className="dash-error">
            <AlertTriangle size={32} />
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-wrapper">
      <div className="dash-header">
        <div className="dash-header-bg" />
        <div className="dash-header-content">
          <div className="dash-breadcrumb">
            <span>Laporan & Checklist</span>
            <span className="dash-breadcrumb-sep">/</span>
            <span className="dash-breadcrumb-current">{pageTitle}</span>
          </div>
          <h2 className="dash-title">{pageTitle}</h2>
          <p className="dash-subtitle">{pageSubtitle}</p>
        </div>
      </div>

      <div className="dash-content">
        {isAbsensiOnly ? (
          /* Mode Laporan Absensi: hanya rekap absensi, tanpa tab lain */
          <TabAbsensi data={data?.absensi} onDateFilter={handleDateFilter} activeFilters={dateFilters} />
        ) : (
          <>
            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
              <TabButton active={activeTab === 'kendaraan'} onClick={() => setActiveTab('kendaraan')} icon={Truck}>
                Kendaraan
              </TabButton>
              <TabButton active={activeTab === 'sparepart'} onClick={() => setActiveTab('sparepart')} icon={Package}>
                Sparepart
              </TabButton>
              <TabButton active={activeTab === 'penggunaan'} onClick={() => setActiveTab('penggunaan')} icon={PackageSearch}>
                Penggunaan Sparepart
              </TabButton>
              <TabButton active={activeTab === 'maintenance'} onClick={() => setActiveTab('maintenance')} icon={Wrench}>
                Maintenance
              </TabButton>
              <TabButton active={activeTab === 'absensi'} onClick={() => setActiveTab('absensi')} icon={Clock}>
                Absensi
              </TabButton>
            </div>

            {/* Tab Content */}
            {activeTab === 'kendaraan' && <TabKendaraan data={data?.kendaraan} />}
            {activeTab === 'sparepart' && <TabSparepart data={data?.sparepart} />}
            {activeTab === 'penggunaan' && <TabPenggunaanSparepart />}
            {activeTab === 'maintenance' && <TabMaintenance data={data?.maintenance} />}
            {activeTab === 'absensi' && <TabAbsensi data={data?.absensi} onDateFilter={handleDateFilter} activeFilters={dateFilters} />}
          </>
        )}
      </div>
    </div>
  );
}
